import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  ArrowLeft, Download, Calendar, CheckCircle2, XCircle,
  Plane, Hotel, MapPin, Clock, Phone, Mail, Globe,
  Star, Utensils, CreditCard, FileText, ScrollText, StickyNote,
  MessageCircle, Users, User, Ship, Copy, Hash, ChevronDown
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { interpolate } from 'flubber';
import { prepareElementForPdf, buildHtml2PdfOptions, PDF_TARGET_WIDTH_PX } from '@/lib/pdfExport';
import ItineraryFlightCard from '@/components/itinerary/ItineraryFlightCard';
import { parseGdsFlights, GdsFlightList } from '@/components/itinerary/GdsFlightCard';
import { calc_price, PAX_CONFIG } from '../utils/pricing';
// Self-hosted (not Google Fonts CDN) — hero accent line only. Bundled with
// the app so it doesn't depend on a 3rd-party CDN being reachable.
import '@fontsource/la-belle-aurore';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ── Page-1 greeting + travel-date helpers (Prompt B1) ───────────────────────
// Abbreviated months ("Jun", "Sep") per the date-format correction.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul',
  'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

// Parse a YYYY-MM-DD (or similar) date string into a LOCAL Date (no TZ shift).
const parseTravelDate = (str) => {
  if (!str) return null;
  const m = String(str).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(str);
  return Number.isNaN(d.getTime()) ? null : d;
};

// "4th – 10th June 2026" (same month) · "28th May – 4th June 2026" (diff month)
// · "28th Dec 2025 – 3rd Jan 2026" (diff year). null/raw fallback when unparseable.
const formatTravelDateRange = (startStr, endStr) => {
  const start = parseTravelDate(startStr);
  const end = parseTravelDate(endStr);
  if (!start) return (startStr || '').trim() || null;
  if (!end) return `${ordinal(start.getDate())} ${MONTHS[start.getMonth()]} ${start.getFullYear()}`;
  const sameYear = start.getFullYear() === end.getFullYear();
  const sameMonth = sameYear && start.getMonth() === end.getMonth();
  if (sameMonth) {
    return `${ordinal(start.getDate())} – ${ordinal(end.getDate())} ${MONTHS[start.getMonth()]} ${end.getFullYear()}`;
  }
  if (sameYear) {
    return `${ordinal(start.getDate())} ${MONTHS[start.getMonth()]} – ${ordinal(end.getDate())} ${MONTHS[end.getMonth()]} ${end.getFullYear()}`;
  }
  return `${ordinal(start.getDate())} ${MONTHS[start.getMonth()]} ${start.getFullYear()} – ${ordinal(end.getDate())} ${MONTHS[end.getMonth()]} ${end.getFullYear()}`;
};

// Full-width greeting (left) + travel dates (right) row below the hero.
// Redesign (Session 1) — the travel-dates block moved to the hero meta row
// overlaid on the cover photo; this now shows the greeting text only.
const GreetingDatesRow = ({ guestName, duration, destination }) => {
  const firstName = (guestName || '').trim().split(' ')[0] || 'there';
  return (
    <div data-testid="greeting-dates-row" style={{ padding: '20px 0 16px' }}>
      <div data-testid="greeting-block">
        <p style={{ fontSize: '20px', fontWeight: 500, color: 'var(--color-text-primary, #0f172a)', margin: 0 }}>
          Hi {firstName},
        </p>
        <p style={{ fontSize: '14px', color: 'var(--color-text-secondary, #475569)', marginTop: '4px' }}>
          Your {duration}-day {destination} quote is ready.
        </p>
      </div>
    </div>
  );
};

// Plain trip-summary columns — no box/background, 0.5px vertical separators.
// (Unused since the Session 1 redesign moved Trip ID/Duration/Route/
// Travellers into the hero meta row, but kept — a future card layout may
// still want this shared primitive.)
const TripSummaryRow = ({ items }) => (
  <div
    data-testid="trip-summary-row"
    className="pdf-no-break"
    style={{ display: 'flex', alignItems: 'flex-start', margin: '4px 0 22px' }}
  >
    {items.map((it, i) => (
      <div
        key={it.label}
        data-testid={it.testId}
        style={{
          flex: 1, padding: '0 14px', textAlign: 'center',
          borderLeft: i === 0 ? 'none' : '0.5px solid var(--color-border-tertiary, #e2e8f0)',
        }}
      >
        <p style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-text-tertiary, #94a3b8)', margin: 0 }}>
          {it.label}
        </p>
        <div style={{ fontSize: '14px', fontWeight: 500, color: 'var(--color-text-primary, #0f172a)', marginTop: '4px' }}>
          {it.value}
        </div>
      </div>
    ))}
  </div>
);

const PAX_BASIS_LABEL = { person: 'per person', couple: 'per couple', family: 'per family', group: 'per group' };

// C4 — one optional add-on / supplement row.
const SupplementRow = ({ s }) => {
  const basisLabel = PAX_BASIS_LABEL[s.price_basis] || (s.price_basis === 'total' ? 'total' : 'per person');
  return (
    <div className="flex items-start justify-between gap-4 py-4" data-testid="supplement-row">
      <div className="min-w-0">
        <p style={{ fontSize: '13px', fontWeight: 500, color: 'var(--color-text-primary, #0f172a)' }}>
          {s.title}
          {s.is_optional !== false && (
            <span style={{ fontSize: '11px', color: 'var(--color-text-tertiary, #94a3b8)', marginLeft: '8px' }}>Optional</span>
          )}
        </p>
        {s.description && (
          <p style={{ fontSize: '12px', color: 'var(--color-text-secondary, #475569)', marginTop: '2px' }}>{s.description}</p>
        )}
        {s.availability_note && (
          <p style={{ fontSize: '11px', color: '#92400E', marginTop: '4px' }}>
            <Clock className="inline h-3 w-3 mr-1" />{s.availability_note}
          </p>
        )}
      </div>
      <div className="text-right shrink-0">
        <p style={{ fontSize: '14px', fontWeight: 500, color: 'var(--color-text-primary, #0f172a)' }}>
          {Number(s.price) > 0 ? `₹${Number(s.price).toLocaleString('en-IN')}` : '—'}
        </p>
        <p style={{ fontSize: '11px', color: 'var(--color-text-tertiary, #94a3b8)' }}>{basisLabel}</p>
      </div>
    </div>
  );
};

// C4 — "Optional Add-ons & Supplement Costs" section.
const SupplementsSection = ({ supplements, isPrintMode }) => {
  const list = Array.isArray(supplements) ? supplements.filter((s) => s && s.title) : [];
  if (list.length === 0) return null;
  // In PDF, only render when at least one supplement is mandatory.
  if (isPrintMode && !list.some((s) => s.is_optional === false)) return null;
  return (
    <section className="px-8 md:px-12 py-12 border-b border-slate-100 pdf-no-break" data-testid="supplements-section">
      <p style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--color-text-tertiary, #94a3b8)', fontWeight: 600 }}>
        Optional Add-ons &amp; Supplement Costs
      </p>
      <div className="mt-4 divide-y divide-slate-100">
        {list.map((s, i) => (
          <SupplementRow key={i} s={s} />
        ))}
      </div>
    </section>
  );
};

// C3 — payment link + WhatsApp/Email. Hidden in PDF (gated by caller).
const PaymentSection = ({ itinerary }) => {
  const [link, setLink] = useState(null);
  const [advance, setAdvance] = useState(null);
  const [amountType, setAmountType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const id = itinerary.id || itinerary._id;
  const paxMode = (itinerary.visible_pax_modes || ['couple'])[0] || 'couple';
  const priced = calc_price(itinerary, paxMode);
  const advPct = itinerary.advance_percentage != null ? Math.round(itinerary.advance_percentage * 100) : 25;
  const optName = (itinerary.pricingOptions || [])[0]?.title || 'Package';
  const clientName = (itinerary.guestName || 'Guest').trim();
  const firstName = clientName.split(' ')[0] || 'there';
  const phone = (itinerary.clientPhone || itinerary.guestPhone || '').replace(/[^\d]/g, '');
  const email = itinerary.clientEmail || itinerary.guestEmail || '';
  const tripName = itinerary.title || itinerary.destination || 'your trip';

  const generate = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/itinerary/${id}/payment-link`, { pax_mode: paxMode });
      setLink(r.data.link);
      setAdvance(r.data.amount);
      setAmountType(r.data.amount_type || 'advance');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate payment link');
    } finally {
      setLoading(false);
    }
  };

  const amountTypeWord = amountType === 'full' ? 'full payment' : amountType === 'custom' ? 'booking amount' : 'advance';
  const msgText = () =>
    `Hi ${firstName}, here's your secure booking link for ${tripName}:\n${link}\nAmount: ₹${(advance || priced.advance).toLocaleString('en-IN')} (${amountTypeWord}).`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      window.prompt('Copy link:', link);
    }
  };
  const whatsapp = () => {
    const m = encodeURIComponent(msgText());
    window.open(`https://wa.me/${phone ? '91' + phone : ''}?text=${m}`, '_blank');
  };
  const emailShare = () => {
    const subject = encodeURIComponent(`Payment Link — ${tripName}`);
    const body = encodeURIComponent(msgText());
    window.open(`mailto:${email}?subject=${subject}&body=${body}`, '_blank');
  };

  return (
    <section className="px-8 md:px-12 py-12 border-b border-slate-100" data-testid="payment-section">
      <p style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--color-text-tertiary, #94a3b8)', fontWeight: 600 }}>
        Secure Your Booking
      </p>
      <div className="mt-4 flex items-start justify-between gap-4">
        <div>
          <p style={{ fontSize: '14px', fontWeight: 500, color: 'var(--color-text-primary, #0f172a)' }}>{optName}</p>
          <p style={{ fontSize: '12px', color: 'var(--color-text-secondary, #475569)' }}>Advance ({advPct}%) · non-refundable</p>
        </div>
        <div className="text-right">
          <p style={{ fontSize: '22px', fontWeight: 500, color: 'var(--color-text-primary, #0f172a)' }} data-testid="payment-advance-amount">
            ₹{(advance != null ? advance : priced.advance).toLocaleString('en-IN')}
          </p>
          <p style={{ fontSize: '11px', color: 'var(--color-text-tertiary, #94a3b8)' }}>of ₹{priced.total.toLocaleString('en-IN')} total</p>
        </div>
      </div>

      <div className="my-5 py-3 flex flex-wrap gap-x-6 gap-y-1 border-t border-b border-slate-100" style={{ fontSize: '12px', color: 'var(--color-text-secondary, #475569)' }}>
        <span>Client: <strong className="font-medium text-slate-800">{clientName}</strong></span>
        {itinerary.displayTripId && <span>Trip ID: <strong className="font-medium text-slate-800">{itinerary.displayTripId}</strong></span>}
        <span>Via: Cashfree · Card / UPI / NetBanking</span>
      </div>

      {!link ? (
        <Button
          type="button"
          onClick={generate}
          disabled={loading}
          className="w-full"
          style={{ background: 'var(--color-background-info, #eff6ff)', color: 'var(--color-text-info, #1d4ed8)', border: '0.5px solid var(--color-border-info, #bfdbfe)' }}
          data-testid="generate-payment-link-btn"
        >
          <CreditCard className="h-4 w-4 mr-2" />
          {loading ? 'Generating…' : 'Generate payment link'}
        </Button>
      ) : (
        <div className="space-y-3" data-testid="payment-link-result">
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 font-mono text-xs text-slate-700 break-all" data-testid="payment-link-box">
            {link}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" variant="outline" onClick={copy} data-testid="payment-copy-btn">
              <Copy className="h-4 w-4 mr-2" />{copied ? 'Copied ✓' : 'Copy'}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={whatsapp} data-testid="payment-whatsapp-btn">
              <MessageCircle className="h-4 w-4 mr-2" />WhatsApp
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={emailShare} data-testid="payment-email-btn">
              <Mail className="h-4 w-4 mr-2" />Email
            </Button>
          </div>
        </div>
      )}
    </section>
  );
};

// Default destination images from Unsplash
const DESTINATION_IMAGES = {
  'bali': 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=1920&q=80',
  'maldives': 'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=1920&q=80',
  'dubai': 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1920&q=80',
  'singapore': 'https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=1920&q=80',
  'thailand': 'https://images.unsplash.com/photo-1528181304800-259b08848526?w=1920&q=80',
  // Item 3 — city-level entries so multi-city trips (e.g. 2N Krabi + 3N
  // Phuket) get a photo specific to THAT leg instead of always falling
  // back to the generic country photo. Checked BEFORE the country-level
  // entries in resolveGroupBanner (city → destination → fallback).
  'krabi': 'https://images.pexels.com/photos/37908150/pexels-photo-37908150.jpeg?auto=compress&cs=tinysrgb&w=1920',
  'phuket': 'https://images.unsplash.com/photo-1594342436424-dda50df715d9?w=1920&q=80',
  'bangkok': 'https://images.unsplash.com/photo-1563492065599-3520f775eeed?w=1920&q=80',
  'koh samui': 'https://images.unsplash.com/photo-1588741145164-494800cdeda4?w=1920&q=80',
  'europe': 'https://images.unsplash.com/photo-1467269204594-9661b134dd2b?w=1920&q=80',
  'switzerland': 'https://images.unsplash.com/photo-1530122037265-a5f1f91d3b99?w=1920&q=80',
  'paris': 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1920&q=80',
  'london': 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=1920&q=80',
  'turkey': 'https://images.unsplash.com/photo-1541432901042-2d8bd64b4a9b?w=1920&q=80',
  'greece': 'https://images.unsplash.com/photo-1533105079780-92b9be482077?w=1920&q=80',
  'mauritius': 'https://images.unsplash.com/photo-1585152968992-d2b9444408cc?w=1920&q=80',
  'kerala': 'https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?w=1920&q=80',
  'kashmir': 'https://images.unsplash.com/photo-1597074866923-dc0589150358?w=1920&q=80',
  'goa': 'https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=1920&q=80',
  'rajasthan': 'https://images.unsplash.com/photo-1477587458883-47145ed94245?w=1920&q=80',
  'japan': 'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=1920&q=80',
  'vietnam': 'https://images.unsplash.com/photo-1528127269322-539801943592?w=1920&q=80',
  'indonesia': 'https://images.unsplash.com/photo-1518548419970-58e3b4079ab2?w=1920&q=80',
  'default': 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=1920&q=80'
};

// Cool captions for destinations
const DESTINATION_CAPTIONS = {
  'bali': 'Where Paradise Meets Culture',
  'maldives': 'Your Private Piece of Paradise',
  'dubai': 'Where Dreams Touch the Sky',
  'singapore': 'The Garden City of Wonders',
  'thailand': 'Land of Smiles Awaits',
  'europe': 'A Journey Through Time',
  'switzerland': 'Nature\'s Masterpiece',
  'paris': 'The City of Love & Lights',
  'london': 'Where History Meets Modern',
  'turkey': 'Where East Meets West',
  'greece': 'Birthplace of Legends',
  'mauritius': 'Heaven on Earth',
  'kerala': 'God\'s Own Country',
  'kashmir': 'Paradise on Earth',
  'goa': 'Sun, Sand & Serenity',
  'rajasthan': 'Land of Kings',
  'japan': 'Where Tradition Meets Tomorrow',
  'vietnam': 'Hidden Treasures Await',
  'default': 'Your Adventure Begins Here'
};

function normalizeDestKey(str) {
  return (str || '').toLowerCase().trim();
}

// BUG FIX — root cause of "same destination, different photo": both
// getDestinationImage/hasCuratedDestinationMatch/getDestinationCaption used
// to loop Object.entries() in DICT INSERTION ORDER and return the FIRST
// substring hit. For a combo string like "Phuket & Krabi" that meant
// 'krabi' (declared earlier in DESTINATION_IMAGES) always won over
// 'phuket', even when the caller's intent/city text pointed at Phuket —
// so the SAME real-world destination could resolve to a different photo
// purely because of incidental key ordering, not because anything was
// random or cached. Fix: exact match first, then the LONGEST matching key
// wins (most specific beats incidental short substrings), independent of
// where it sits in the dict. Deterministic and stable across renders/itineraries.
function makeDestinationMatcher(dict) {
  const keysByLenDesc = Object.keys(dict).filter((k) => k !== 'default').sort((a, b) => b.length - a.length);
  return (destination) => {
    const key = normalizeDestKey(destination).split(',')[0].trim();
    if (!key) return null;
    if (dict[key] !== undefined) return key;
    return keysByLenDesc.find((k) => key.includes(k)) || null;
  };
}
const matchImageKey = makeDestinationMatcher(DESTINATION_IMAGES);
const matchCaptionKey = makeDestinationMatcher(DESTINATION_CAPTIONS);

function getDestinationImage(destination) {
  const k = matchImageKey(destination);
  return k ? DESTINATION_IMAGES[k] : DESTINATION_IMAGES.default;
}

// Session 2 — hoisted so the module-level day/destination-grouping helpers
// below can reuse the exact same "is this really a usable image URL" check
// the hero uses (no broken-image icons, ever).
const isResolvableImageUrl = (url) => typeof url === 'string' && (/^https?:\/\//i.test(url) || url.startsWith('/api/'));

// Redesign FIX #0 — the hero must never fall back to a generic, unrelated
// stock photo. It's either a real photo (agent-uploaded, or a genuine
// curated match for THIS destination) or a clean typographic/branded
// treatment — never a random "default" travel photo pretending to be this
// trip's destination.
function hasCuratedDestinationMatch(destination) {
  return !!matchImageKey(destination);
}

function getDestinationCaption(destination) {
  const k = matchCaptionKey(destination);
  return k ? DESTINATION_CAPTIONS[k] : DESTINATION_CAPTIONS.default;
}

// Phase B1 FIX 8 — pretty-print hotel check-in/out dates as `DD Mon YYYY`.
// Accepts any value the backend may send (ISO `2026-05-22`, JS Date, plain
// string already formatted). Falls back to the input untouched if parsing
// fails.
function formatItinDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  const day = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleString('en-GB', { month: 'short' });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
}

// Returns the integer night-count between two ISO/date strings, or null
// if either is missing/invalid/zero/negative.
function getHotelNights(checkIn, checkOut) {
  if (!checkIn || !checkOut) return null;
  const cin = new Date(checkIn);
  const cout = new Date(checkOut);
  if (isNaN(cin.getTime()) || isNaN(cout.getTime())) return null;
  const diff = Math.round((cout - cin) / 86400000);
  return diff > 0 ? diff : null;
}

// Phase B1 FIX 9 — INR price with no decimals (₹2,89,921.8 → 2,89,922).
function formatPrice(amount) {
  if (amount === null || amount === undefined || amount === '') return '';
  const num = parseFloat(amount);
  if (isNaN(num)) return String(amount);
  return Math.round(num).toLocaleString('en-IN');
}

// Session 2 — optional per-day date label (only when travelStartDate is
// set on the itinerary; many aren't, so the collapsed-card date is best
// effort and simply omitted when unavailable).
function addDaysToDate(dateStr, n) {
  const d = parseTravelDate(dateStr);
  if (!d) return null;
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

// ── Session 2 — destination-banner day grouping ─────────────────────────
// Days carry no date field of their own, and real-world itineraries very
// often have travelStartDate unset — so grouping is SEQUENCE-based (per
// user direction), not calendar-date matching. Hotels are already entered
// in visit order with .city + check-in/out; we take each hotel's night
// count as its "share" of the day sequence and bucket days into contiguous
// destination legs in that same order via cumulative-fraction rounding.
// This degrades gracefully: 0-1 real hotel → a single group (no banner).
// ITEM 4 fix — when a hotel's checkInDate/checkOutDate aren't filled in
// (common for draft/template itineraries with no fixed travel dates), the
// share silently fell back to "1 night" for EVERY such hotel, discarding
// the agent's real night split entirely (e.g. a stated "2N Krabi + 3N
// Phuket" rendered as an even 3-3 day split). Now prefers the explicit
// `hotel.nights` override (added alongside the date fields in
// HotelEditor.js, same convention cruises already used) before falling
// back to the date-diff, and only defaults to 1 when NEITHER is set.
// Checkout-day boundary fix (2026-09): a hotel's `nights` count is its OWN
// night count only — the checkout/transfer day physically belongs to the
// day you travel to and experience the NEXT destination, not the one you're
// leaving. So every NON-FINAL city gets exactly its own night-count of
// days; the FINAL city absorbs everything left over (its own nights, PLUS
// the one genuinely-extra day at the very end of the trip that has no
// "next" city to hand off to). This replaces the old proportional
// cumulative-fraction rounding, which let earlier cities' rounding
// surplus leak an extra day away from later cities — the wrong direction.
function groupDaysByDestination(days, hotels, fallbackDestination) {
  const totalDays = (days || []).length;
  if (totalDays === 0) return [];
  const validHotels = (hotels || []).filter((h) => h && h.city && (h.accommodation_type || 'hotel') !== 'cruise');
  if (validHotels.length === 0) {
    return [{ city: fallbackDestination || null, days: days.map((day) => ({ day })) }];
  }
  const shares = validHotels.map((h) => {
    const n = Number(h.nights) || getHotelNights(h.checkInDate, h.checkOutDate);
    return n && n > 0 ? n : 1;
  });
  const groups = [];
  let start = 0;
  validHotels.forEach((h, i) => {
    const isLast = i === validHotels.length - 1;
    let end;
    if (isLast) {
      end = totalDays;
    } else {
      const remainingHotelsAfter = validHotels.length - 1 - i; // count includes the last hotel
      end = Math.min(start + shares[i], totalDays - remainingHotelsAfter);
      end = Math.max(end, start + 1); // every city gets >=1 day if any remain
    }
    if (start >= totalDays) return;
    const slice = days.slice(start, Math.min(end, totalDays)).map((day) => ({ day }));
    if (slice.length === 0) return;
    groups.push({ city: h.city, days: slice });
    start = end;
  });
  return groups;
}

// Banner image for a destination leg — FEATURE: an explicit per-itinerary
// manual override (set in the editor, keyed by this leg's city/destination
// text) always wins first. Then the pre-existing implicit signal (an
// agent-uploaded day photo reused as the leg's banner), then a curated
// CITY match (e.g. Krabi vs Phuket), then the broader curated DESTINATION
// match (e.g. Thailand), else a clean typographic fallback. Never a random
// unrelated stock photo (same FIX #0 rule as the hero).
function resolveGroupBanner(group, destination, destinationPhotoOverrides) {
  // Override key must match HotelEditor's cityKeyFor() exactly (plain
  // lowercase+trim, no comma-splitting) — testing_agent iter_130 flagged a
  // mismatch risk here (curated-lookup keys DO split on comma, override
  // keys must NOT, since a city field like "Krabi" never has a comma but
  // treating it the same way as the curated matcher could silently break
  // the override for edge-case city text).
  const overrideKey = normalizeDestKey(group.city);
  const override = destinationPhotoOverrides && overrideKey && destinationPhotoOverrides[overrideKey];
  if (isResolvableImageUrl(override)) {
    return { type: 'photo', url: override.startsWith('/') ? `${BACKEND_URL}${override}` : override };
  }
  const uploaded = group.days.find((d) => isResolvableImageUrl(d.day.imageUrl));
  if (uploaded) {
    const url = uploaded.day.imageUrl.startsWith('/') ? `${BACKEND_URL}${uploaded.day.imageUrl}` : uploaded.day.imageUrl;
    return { type: 'photo', url };
  }
  if (hasCuratedDestinationMatch(group.city)) return { type: 'photo', url: getDestinationImage(group.city) };
  if (hasCuratedDestinationMatch(destination)) return { type: 'photo', url: getDestinationImage(destination) };
  return { type: 'fallback' };

}

// Hotel photo — Session 3a corrections pass #2: ONLY a real agent-uploaded
// photo counts. A curated destination stock photo is a decorative
// substitute for the specific hotel and is no longer used here (that
// pattern stays reserved for the day-banner use case, which is genuinely
// about the destination, not a specific property). No photo => no image
// at all — a clean text-only card, never an invented placeholder.
function resolveHotelPhoto(hotel) {
  if (isResolvableImageUrl(hotel.imageUrl)) {
    const url = hotel.imageUrl.startsWith('/') ? `${BACKEND_URL}${hotel.imageUrl}` : hotel.imageUrl;
    return { type: 'photo', url };
  }
  return { type: 'none' };
}

function ItineraryPreviewPage({ printMode = false, showPriceProp = true }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [itinerary, setItinerary] = useState(null);
  const [companySettings, setCompanySettings] = useState(null);
  // Structured flight vouchers for the linked trip. The itinerary doc itself
  // only stores a free-text `flightDetails` blob; the airline-logo flight
  // card needs structured segments (with IATA airlineCode), which live on the
  // trip's flight vouchers. We fetch them here so the customer-facing preview
  // + PDF can render the same branded ItineraryFlightCard the builder shows.
  const [flightVouchers, setFlightVouchers] = useState([]);
  const [tripPricingOptions, setTripPricingOptions] = useState([]);
  // Expired/Lost/Cancelled bug fix — set when the backend returns 410 for a
  // dead/stale quote. Renders a clear message instead of a blank page or a
  // silent navigate(-1) with no browser history to go back to.
  const [blockedReason, setBlockedReason] = useState(null);
  // Fetch-reliability fix (2026-10-06) — no request had a timeout, so a slow/
  // stalled connection (seen intermittently in this environment, independent
  // of flight-count or itinerary size — the backend itself answers in <1s
  // every time it's hit directly) left the UI on the loading spinner forever
  // with zero feedback. Every call below now has an explicit timeout, the
  // whole fetch races against an outer timeout as a last-resort net, and a
  // failure now shows a Retry screen instead of hanging or silently bouncing
  // the user back with `navigate(-1)` (which has no history on a fresh tab).
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    setLoadError(false);
    const REQUEST_TIMEOUT = 15000;
    const OVERALL_TIMEOUT = 25000;
    let overallTimedOut = false;
    const overallTimer = setTimeout(() => { overallTimedOut = true; }, OVERALL_TIMEOUT);
    try {
      const [itinRes, settingsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/itineraries/${id}`, { timeout: REQUEST_TIMEOUT }),
        // Public branding only — /api/settings is auth-protected (it also
        // carries bank/financial fields). The client share-preview is public.
        // Never let a company-branding failure (e.g. 401 once an API key
        // exists) reject the whole load and bounce the page — degrade to no
        // branding instead.
        axios.get(`${BACKEND_URL}/api/public/company`, { timeout: REQUEST_TIMEOUT }).catch(() => ({ data: {} }))
      ]);
      if (overallTimedOut) throw new Error('timed out');
      setItinerary(itinRes.data);
      setCompanySettings(settingsRes.data);
      const tripId = itinRes.data?.tripId;
      if (tripId) {
        try {
          const vRes = await axios.get(`${BACKEND_URL}/api/vouchers`, { params: { trip_id: tripId }, timeout: REQUEST_TIMEOUT });
          const flights = (Array.isArray(vRes.data) ? vRes.data : []).filter(
            (v) => v.type === 'flight' && (v.flight?.segments?.length || 0) > 0,
          );
          setFlightVouchers(flights);
        } catch (_) {
          setFlightVouchers([]);
        }
        // Directive 2 (2026-09-21) — Trip-level dynamic pricingOptions
        // (QuoteOption shape, distinct from this itinerary's own
        // price-only pricingOptions above). Same graceful-degrade pattern
        // as the flight vouchers fetch: a real anonymous client without an
        // agent session simply won't see this section.
        try {
          const tRes = await axios.get(`${BACKEND_URL}/api/quotes/${tripId}`, { timeout: REQUEST_TIMEOUT });
          setTripPricingOptions(Array.isArray(tRes.data?.pricingOptions) ? tRes.data.pricingOptions : []);
        } catch (_) {
          setTripPricingOptions([]);
        }
      }
    } catch (error) {
      if (error?.response?.status === 410) {
        setBlockedReason(error.response.data?.detail || 'This quote is no longer available.');
      } else {
        setLoadError(true);
      }
    } finally {
      clearTimeout(overallTimer);
      setLoading(false);
    }
  };

  const [pdfLoading, setPdfLoading] = useState(false);
  const [clientMode, setClientMode] = useState(false);
  const [mobileDay, setMobileDay] = useState(1);
  const clientFacing = printMode || clientMode;
  // 1b — the 2-option "which option do I want" client selection + the
  // itemised price-breakup toggle are retired (single lump-sum price now;
  // never a breakdown). Only the pax-mode tabs remain.
  const [paxMode, setPaxMode] = useState('couple');
  // A3 — when the itinerary loads, snap paxMode to the agent's first visible
  // pax tab (default 'couple') if the current mode isn't allowed.
  useEffect(() => {
    if (!itinerary) return;
    const vm = getVisiblePaxModes(itinerary);
    setPaxMode((prev) => (vm.includes(prev) ? prev : (vm[0] || 'couple')));
  }, [itinerary]);
  // Phase B2 FEATURE 3 — "Include price in PDF" toggle. Seed from
  // `showPriceProp` so the public-route wrapper can pass the URL query
  // param value through.
  const [showPrice, setShowPrice] = useState(showPriceProp);

  // Returns a base64 data-URI for the company logo so we can stamp it onto every
  // PDF page via jsPDF (avoids CORS issues during PDF generation).
  const fetchLogoDataUri = async (url) => {
    try {
      const res = await fetch(url, { mode: 'cors' });
      const blob = await res.blob();
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      return null;
    }
  };

  // NEW Playwright-backed renderer (Phase 5 migration, Feb 2026)
  // Same auth-forwarding pattern as the voucher migration: generatePDF()
  // hits POST /api/pdf/render with the protected itinerary preview URL.
  // The util ships httpOnly cookies (credentials:'include') AND the
  // localStorage Bearer token in the body so the headless Chromium can
  // replay auth on first paint.
  const handleDownloadPDF = async (mode = 'compare') => {
    setPdfLoading(true);
    try {
      const refPrefix = itinerary?.displayTripId || itinerary?.guestName || id;
      const safeRef = String(refPrefix).replace(/[^a-zA-Z0-9-_]/g, '-');
      const destination = itinerary?.destination || itinerary?.title || 'Itinerary';
      const safeDestination = String(destination).replace(/[^a-zA-Z0-9-_]/g, '-');
      const filename = `${safeRef}-${safeDestination}-${mode}.pdf`;

      // Step 3 — server-rendered PDF (Playwright) with the requested mode.
      // Task 6 — forward the current price-visibility toggle so the PDF hides
      // prices when the agent turned them off (was hardcoded to show).
      const resp = await fetch(`${BACKEND_URL}/api/itinerary/${id}/pdf?mode=${mode}&show_price=${showPrice ? 'true' : 'false'}`, { method: 'GET' });
      if (!resp.ok) throw new Error(await resp.text());
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch (error) {
      console.error('Itinerary PDF failed:', error);
      // Surface the backend's real detail (e.g. a 404 "itinerary not found")
      // instead of a generic message that hides what actually went wrong.
      let message = 'Failed to generate PDF. Please try again.';
      try {
        const parsed = JSON.parse(error.message);
        if (parsed?.detail) message = parsed.detail;
      } catch (_) { /* not JSON — keep generic message */ }
      toast.error(message);
    } finally {
      setPdfLoading(false);
    }
  };

  /* OLD RENDERER — replaced by Playwright (Phase 5 migration, Feb 2026).
     Kept commented out for emergency rollback only. Do not delete.

  const handleDownloadPDF = async () => {
    setPdfLoading(true);
    try {
      const element = document.getElementById('preview-container');
      if (!element) {
        toast.error('Preview content not found');
        return;
      }

      // STEP 1 — wait for web fonts + inline remote images as base64.
      // html2canvas races font loads and silently 404s remote images in
      // the snapshot, which causes the misaligned/empty-image bug. This
      // helper handles both concerns in parallel.
      await prepareElementForPdf(element);

      // STEP 2 — temporarily lock the container to the A4 capture width
      // so html2canvas takes a deterministic snapshot, then restore the
      // responsive width after rendering.
      const prevWidth = element.style.width;
      const prevMaxWidth = element.style.maxWidth;
      element.style.width = `${PDF_TARGET_WIDTH_PX}px`;
      element.style.maxWidth = `${PDF_TARGET_WIDTH_PX}px`;

      const html2pdf = (await import('html2pdf.js')).default;
      // Pre-fetch the logo as data URI so jsPDF can stamp it on every page.
      const logoDataUri = await fetchLogoDataUri(logoUrl);

      const opt = {
        ...buildHtml2PdfOptions({
          filename: `${itinerary?.title || 'itinerary'}.pdf`,
          pageMargin: [22, 8, 18, 8], // top: 22 mm for stamped header strip on page 2+
        }),
      };

      try {
        // Use html2pdf's worker to access jsPDF after rendering, then stamp the
        // header (brand strip) + footer (logo + page numbers) on every page.
        const worker = html2pdf().set(opt).from(element).toPdf();
        await worker.get('pdf').then((pdf) => {
          const pageCount = pdf.internal.getNumberOfPages();
          const pageWidth = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();
          const brandName = companySettings?.companyName || 'Travcations';
          const itinTitle = (itinerary?.title || 'Itinerary').slice(0, 60);
          for (let i = 1; i <= pageCount; i++) {
            pdf.setPage(i);

            // HEADER on page 2+ — matches the voucher pattern for consistency.
            if (i > 1) {
              try { pdf.setFillColor(52, 208, 203); pdf.rect(0, 0, pageWidth, 3, 'F'); } catch (e) {}
              pdf.setFontSize(11);
              pdf.setTextColor(17, 24, 39);
              pdf.text(brandName, 12, 11);
              pdf.setFontSize(8);
              pdf.setTextColor(110, 116, 130);
              pdf.text(itinTitle, pageWidth - 12, 11, { align: 'right' });
              pdf.setDrawColor(230, 232, 236);
              pdf.setLineWidth(0.2);
              pdf.line(12, 14, pageWidth - 12, 14);
            }

            // Subtle hairline above the footer
            pdf.setDrawColor(220, 220, 220);
            pdf.setLineWidth(0.1);
            pdf.line(12, pageHeight - 10, pageWidth - 12, pageHeight - 10);

            // Brand name on the left of footer for symmetry
            pdf.setFontSize(8);
            pdf.setTextColor(110, 116, 130);
            pdf.text(brandName, 12, pageHeight - 5);

            // Page X of N — bottom centre, sleek slate
            pdf.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 5, { align: 'center' });

            // Logo — bottom right (skip first page; cover already has it)
            if (logoDataUri && i > 1) {
              try {
                pdf.addImage(logoDataUri, 'PNG', pageWidth - 30, pageHeight - 9, 20, 7, undefined, 'FAST');
              } catch (e) {}
            }
          }
        });
        await worker.save();
        toast.success('PDF downloaded!');
      } finally {
        // ALWAYS restore the container width so the on-screen preview
        // returns to its responsive layout.
        element.style.width = prevWidth;
        element.style.maxWidth = prevMaxWidth;
      }
    } catch (error) {
      toast.error('Failed to generate PDF');
    } finally {
      setPdfLoading(false);
    }
  };
  */

  const handleWhatsAppShare = () => {
    // Step 8 — clean branded client URL (crm.travcations.com/{ref}/preview).
    // Provided by the API as public_url; falls back to the current origin.
    const url = itinerary?.public_url
      || `${window.location.origin}/${itinerary?.public_ref || id}/preview`;
    const text = `Check out this amazing travel itinerary: ${itinerary?.title} - ${itinerary?.destination}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text + '\n' + url)}`;
    window.open(whatsappUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="loading-spinner">
        <TravelLoadingAnimation />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex items-center justify-center min-h-screen px-6" data-testid="itinerary-load-error">
        <div className="text-center space-y-4 max-w-sm">
          <p className="text-lg font-semibold text-slate-800">Couldn't load this itinerary</p>
          <p className="text-sm text-slate-500">The connection was slow or interrupted. Please try again.</p>
          <button
            type="button"
            onClick={fetchData}
            className="px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors"
            data-testid="itinerary-load-retry-button"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (blockedReason) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 px-4" data-testid="itinerary-blocked-screen">
        <div className="max-w-md text-center space-y-3 bg-white border border-slate-200 rounded-2xl px-8 py-10 shadow-sm">
          <p className="text-lg font-semibold text-slate-800" data-testid="itinerary-blocked-message">{blockedReason}</p>
          <p className="text-sm text-slate-500">If you believe this is a mistake, please reach out to your travel agent directly.</p>
        </div>
      </div>
    );
  }

  if (!itinerary) return null;

  const { 
    destination, duration, description, days, hotels,
    inclusions, exclusions, hotelDisclaimer, flightDetails, 
    termsAndConditions, paymentPolicy, notes, paymentDetails,
    heroImageUrl, heroCaption, displayTripId, travelStartDate, travelEndDate,
    adults, children, infants, totalCost, guestName,
    quoteBasis, curatorName, curatorPhone, curatorEmail, pricingOptions, quoteNotes,
    highlights,
    paymentLink, paymentAmount,
    cruise_inclusions, cruise_exclusions,
    destinationPhotoOverrides,
  } = itinerary;

  // Correction 3 — does any accommodation in this itinerary sail?
  const hasCruise = (hotels || []).some((h) => (h?.accommodation_type || 'hotel') === 'cruise');

  // Fallback flight cards: when the trip has no structured FlightVoucher we
  // parse the free-text GDS blob into boarding-pass directions instead of
  // dumping the raw string.
  const gdsDirections = parseGdsFlights(flightDetails || '');

  // 1b — Accommodation option comparison retired: pricingOptions is capped
  // to a single entry now (ItineraryBuilder), so a real 2-option itinerary
  // can no longer exist. regularStays = every accommodation, always.
  const allHotels = hotels || [];
  const regularStays = allHotels;

  // Session 2 — group days into destination legs for the banner-grouped,
  // collapsible day-card layout (live preview only; PDF path unchanged
  // pending Session 5's PDF-parity pass).
  const destGroups = (days && days.length > 0)
    ? groupDaysByDestination(days, allHotels, destination)
    : [];
  const showDestBanners = destGroups.length > 1;

  // Single-quote cover pricing: derive a numeric price from pricingOptions[0]
  // (its `.price` or the legacy free-text items[0].price) for the lump-sum card.
  const firstOptionPrice = (() => {
    const po = (pricingOptions || [])[0];
    if (!po) return 0;
    let p = po.price != null && po.price !== '' ? Number(po.price) : NaN;
    if (Number.isNaN(p)) p = extractAmount((po.items?.[0]?.price) || '') || 0;
    return Number.isNaN(p) ? 0 : p;
  })();
  const coverEntries = ((pricingOptions || []).length > 0 && firstOptionPrice > 0)
    ? [{ hotel: regularStays[0] || {}, optionNum: 1, amount: firstOptionPrice }]
    : [];

  const companyName = companySettings?.companyName || 'Travcations';

  // Resolve the logo URL - handle both /uploads/ and /api/uploads/ paths
  const resolveLogoUrl = () => {
    const logoPath = companySettings?.logoUrl || '/api/uploads/travcations-logo.png';
    // Ensure the path starts with /api/uploads
    if (logoPath.startsWith('/uploads/')) {
      return `${BACKEND_URL}/api${logoPath}`;
    }
    if (logoPath.startsWith('/api/')) {
      return `${BACKEND_URL}${logoPath}`;
    }
    return `${BACKEND_URL}/api/uploads/travcations-logo.png`;
  };
  const logoUrl = resolveLogoUrl();
  
  // Get hero image - prioritize custom uploaded, then auto-fetch based on
  // destination. BUG FIX — this was a plain truthy OR with no validity check:
  // a malformed heroImageUrl (e.g. a bare filename with no protocol/path,
  // found on a real itinerary — likely pasted from a WordPress media
  // filename instead of a real URL) rendered as a permanently blank hero
  // with NO fallback ever kicking in, since the string was truthy. Now we
  // only trust it if it actually looks like a resolvable URL/path.
  const hasCustomHero = isResolvableImageUrl(heroImageUrl);
  const hasCuratedMatch = hasCuratedDestinationMatch(destination);
  const hasHeroPhoto = hasCustomHero || hasCuratedMatch;
  const backgroundImage = hasCustomHero ? heroImageUrl : (hasCuratedMatch ? getDestinationImage(destination) : null);
  const caption = heroCaption || getDestinationCaption(destination);
  const destinationFirstWord = (destination || 'Trip').split(',')[0].trim().split(' ')[0];
  const heroDateRange = formatTravelDateRange(travelStartDate, travelEndDate);
  
  // Calculate total pax
  const totalPax = (adults || 0) + (children || 0) + (infants || 0);

  // FIX 5 — section jump nav. Hidden only when ?print=1 (the PDF render adds
  // it) so the nav still helps clients on the live/share view. Pills only show
  // for sections that actually render on this itinerary.
  const isPrintMode = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('print') === '1';
  const navSections = [
    { id: 'accommodation', label: 'Accommodation', show: hotels && hotels.length > 0 },
    { id: 'itinerary', label: 'Itinerary', show: days && days.length > 0 },
    { id: 'inclusions', label: 'Inclusions', show: (inclusions?.length > 0 || exclusions?.length > 0) },
    { id: 'flights', label: 'Flights', show: true },
    { id: 'terms', label: 'Terms', show: !!termsAndConditions },
    { id: 'payment', label: 'Payment', show: !!paymentPolicy },
    { id: 'notes', label: 'Notes', show: !!notes && !clientFacing },
  ].filter((s) => s.show);

  // Mobile-only sub-tab pill nav (Overview/Flights/Hotels/Itinerary/Inclusions/
  // Pricing). Only sections present on this itinerary are shown.
  const mobileNavSections = [
    { id: 'overview', label: 'Overview', show: true },
    { id: 'flights', label: 'Flights', show: true },
    { id: 'accommodation', label: 'Hotels', show: hotels && hotels.length > 0 },
    { id: 'itinerary', label: 'Itinerary', show: days && days.length > 0 },
    { id: 'inclusions', label: 'Inclusions', show: (inclusions?.length > 0 || exclusions?.length > 0) },
    // Corrections session — "Pricing" pill removed: price now lives ONLY in
    // the always-visible <StickyPricePanel/>, so a jump-to-pricing tab is
    // redundant on the live page (and its #pricing anchor is print-only now).
  ].filter((s) => s.show);

  // TASK 3 — flights ordered date-wise (Onward → Return / multi-city sequential).
  const flightVoucherTime = (v) => {
    const segs = v?.flight?.segments || [];
    let min = Infinity;
    for (const s of segs) { const t = Date.parse(s?.departureDate || ''); if (!Number.isNaN(t)) min = Math.min(min, t); }
    return min;
  };
  const sortedFlightVouchers = [...flightVouchers].sort((a, b) => flightVoucherTime(a) - flightVoucherTime(b));

  return (
    <div className={`max-w-5xl mx-auto space-y-6 pb-12 ${!isPrintMode && showPrice ? 'pb-24 md:pb-12' : ''}`} data-testid="itinerary-preview">
      {/* Print/PDF rules — keep major sections from splitting across pages and
          guarantee white backgrounds (kills any leftover dark gradients in PDF). */}
      <style>{`
        /* Phase 5 fix: removed @media print { @page { size: A4; margin: 0; } }
           which zeroed-out Playwright's PDF margins (top:64px, bottom:52px)
           and caused content to overlap the Playwright header/footer strips.
           Source of truth for page margins now lives in pdf_service.py
           PDF_DEFAULTS — single point of truth. */
        #preview-container .pdf-no-break {
          page-break-inside: avoid;
          break-inside: avoid;
        }
        #preview-container .pdf-page-break-before { page-break-before: always; }
        #preview-container [data-testid="last-page-footer"] { background: #ffffff !important; }
        /* FIX 5 — section jump nav: smooth-scroll offset + hidden scrollbar */
        #preview-container section[id] { scroll-margin-top: 96px; }
        [data-testid="section-jump-nav"]::-webkit-scrollbar { display: none; }
        /* ---- Step 3 / Step 7 — print/PDF pagination ---- */
        #preview-container .print-only { display: none; }
        @media print {
          [data-testid="section-jump-nav"],
          [data-testid="pricing-controls"],
          [data-testid="pricing-recommendation-reason"],
          .option-selector, .pax-toggle, .breakup-switch,
          .payment-link-section, .rec-panel { display: none !important; }
          .print-only { display: block !important; }
          .cruise-card, .pricing-section, .cruise-inc-block,
          .inclusion-block, .note-callout, .pdf-no-break,
          .day-card, .bp-card, .hotel-card, .terms-block,
          .compare-block, .confirm-block {
            break-inside: avoid;
            page-break-inside: avoid;
          }
          .section-flights, .section-terms {
            break-before: page;
            page-break-before: always;
          }
          .section-heading, .day-label {
            break-after: avoid;
            page-break-after: avoid;
          }
          .card, .bp-card { background: #fff !important; box-shadow: none !important; }
          p { orphans: 3; widows: 3; }
        }
        /* Redesign (Session 1) — General Sans is the brand font for this
           page (matches travcations.com); overrides the CRM-wide global
           Inter/Playfair Display rules (src/index.css) which otherwise win
           because a directly-targeted element rule always beats an
           inherited value, regardless of an ancestor's inline style. The
           La Belle Aurore hero accent stays untouched — it's set inline on
           that one element only. */
        [data-testid="itinerary-preview"] {
          font-family: 'General Sans', sans-serif;
        }
        [data-testid="itinerary-preview"] h1,
        [data-testid="itinerary-preview"] h2,
        [data-testid="itinerary-preview"] h3,
        [data-testid="itinerary-preview"] h4,
        [data-testid="itinerary-preview"] h5,
        [data-testid="itinerary-preview"] h6,
        [data-testid="itinerary-preview"] button,
        [data-testid="itinerary-preview"] input,
        [data-testid="itinerary-preview"] select,
        [data-testid="itinerary-preview"] textarea {
          font-family: inherit;
        }
      `}</style>
      {/* Header Controls - hidden in PDF (printMode) and in CSS @media print */}
      {!printMode && (
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 md:sticky md:top-0 bg-white/95 backdrop-blur-sm z-40 py-4 px-2 -mx-2 border-b border-slate-100 print:hidden" data-testid="preview-controls" id="preview-controls">
        <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-2" />Back to Editor
        </Button>
        
        <div className="flex items-center gap-3">
          {/* Client Mode Toggle */}
          <button
            onClick={() => setClientMode(!clientMode)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all border ${
              clientMode 
                ? 'bg-violet-600 text-white border-violet-600 shadow-md' 
                : 'bg-white text-slate-600 border-slate-200 hover:border-violet-300'
            }`}
            data-testid="client-mode-toggle"
          >
            <Users className="h-4 w-4" />
            {clientMode ? 'Client View' : 'Internal View'}
          </button>

          {/* Agent-only tools — hidden while previewing as a client so the
              agent sees exactly what the customer would (no share/download). */}
          {!clientMode && (
          <>
          {/* Phase B2 FEATURE 3 — Include-price toggle. Hides all price
              displays in the generated PDF (and live preview) when off. */}
          <div
            className="flex items-center gap-2 px-3 py-1.5 rounded-xl border border-slate-200 bg-white"
            data-testid="show-price-wrapper"
          >
            <span className="text-xs font-medium text-slate-600 select-none">Include price</span>
            <button
              type="button"
              onClick={() => setShowPrice((v) => !v)}
              aria-pressed={showPrice}
              aria-label="Toggle include price"
              data-testid="show-price-toggle"
              style={{
                width: '40px',
                height: '22px',
                borderRadius: '11px',
                background: showPrice ? '#0891b2' : '#d1d5db',
                cursor: 'pointer',
                position: 'relative',
                transition: 'background 0.2s',
                border: 'none',
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: '2px',
                  left: showPrice ? '20px' : '2px',
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  background: '#ffffff',
                  transition: 'left 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  display: 'block',
                }}
              />
            </button>
          </div>

          <Button 
            variant="outline" 
            onClick={handleWhatsAppShare} 
            className="rounded-xl border-green-500 text-green-600 hover:bg-green-50"
            data-testid="whatsapp-share-btn"
          >
            <MessageCircle className="h-4 w-4 mr-2" />
            Share on WhatsApp
          </Button>
          
          <Button 
            onClick={() => handleDownloadPDF('compare')}
            disabled={pdfLoading}
            className="bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-xl shadow-lg hover:shadow-xl transition-all" 
            data-testid="download-pdf-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            {pdfLoading ? 'Generating...' : 'Download PDF (both options)'}
          </Button>
          {itinerary?.recommended_option && (
            <Button 
              onClick={() => handleDownloadPDF(`option${itinerary.recommended_option}`)}
              disabled={pdfLoading}
              variant="outline"
              className="rounded-xl border-amber-500 text-amber-700 hover:bg-amber-50" 
              data-testid="download-pdf-confirmed-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Download PDF (confirmed)
            </Button>
          )}
          </>
          )}
        </div>
      </div>
      )}

      {/* Mobile-only sticky section pill nav (replaces desktop sub-tabs on phones) */}
      {!isPrintMode && <MobileSectionNav sections={mobileNavSections} />}

      {/* Preview Container */}
      <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200" id="preview-container">
        <span id="overview" style={{ scrollMarginTop: '60px' }} />
        
        {/* ===== PAGE 1: COVER PAGE — FIX #0 redesign =====
            Phase B1 FIX 6: pageBreakAfter forces the next section
            (Accommodation) to always start on a fresh page, so the
            cover stays on page 1.
            Redesign (Session 1) — trip name, dates, trip ID, traveller
            count and curator contact now live OVERLAID on the hero photo
            itself (moved here from the plain white "trip summary" row +
            separate curator block that used to sit below it), for a
            photo-led first impression instead of a text header. */}
        <div
          className="flex flex-col"
          style={{ pageBreakAfter: 'always', breakAfter: 'page', fontFamily: "'General Sans', sans-serif" }}
        >
          {/* HERO — full-bleed destination photo (or, when none exists for
              this destination and the agent hasn't uploaded one, a clean
              branded/typographic treatment — never a random unrelated stock
              photo, and never a broken image).
              Corrections session (2026-08-27) — the print/PDF path keeps the
              ORIGINAL Session-1 hero verbatim (PDF parity deferred to
              Session 5); the live web view gets a fully re-art-directed
              cinematic hero (design_agent brief: "wow on open"), centered
              editorial composition, 3-layer teal/black scrim so a plain
              white logo (no plate) always reads, glass meta chips. */}
          {isPrintMode ? (
          <div
            data-testid="itinerary-hero"
            className="relative min-h-[440px] md:min-h-[540px] bg-cover bg-center flex flex-col"
            style={hasHeroPhoto ? { backgroundImage: `url(${backgroundImage})` } : { background: 'linear-gradient(160deg, #134E4A 0%, #0F766E 100%)' }}
          >
            {hasHeroPhoto ? (
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-black/10" />
            ) : (
              <div
                className="absolute inset-0 opacity-[0.07]"
                style={{ backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)', backgroundSize: '20px 20px' }}
              />
            )}

            {/* Logo at Top Left */}
            <div className="absolute top-6 left-6 z-10">
              <img 
                src={logoUrl}
                alt={companyName}
                className="h-16 w-auto rounded-xl shadow-lg bg-white/90 p-1.5"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div style={{display: 'none'}} className="w-14 h-14 bg-white/95 rounded-xl flex items-center justify-center shadow-lg">
                <Globe className="h-7 w-7 text-teal-600" />
              </div>
            </div>

            {/* Trip name + meta, overlaid bottom-left */}
            <div className="relative z-10 mt-auto px-6 md:px-10 pt-20 pb-7 md:pb-9">
              <p
                data-testid="hero-accent-line"
                className="text-2xl md:text-4xl text-amber-200/90 -rotate-1 mb-1"
                style={{ fontFamily: "'La Belle Aurore', cursive" }}
              >
                Your {destinationFirstWord} Escape
              </p>
              <h1 data-testid="hero-destination-title" className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight drop-shadow-md">
                {destination}
              </h1>
              {caption && (
                <p className="text-white/70 italic text-sm md:text-base mt-2">&ldquo;{caption}&rdquo;</p>
              )}

              <div data-testid="hero-meta-row" className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-5 text-white/90 text-xs md:text-sm">
                {heroDateRange && (
                  <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />{heroDateRange}</span>
                )}
                {displayTripId && (
                  <span className="inline-flex items-center gap-1.5"><Hash className="h-3.5 w-3.5" />{displayTripId}</span>
                )}
                <span className="inline-flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5" />
                  {totalPax} Traveller{totalPax === 1 ? '' : 's'}
                </span>
              </div>

              {(curatorName || curatorPhone || curatorEmail) && (
                <div data-testid="hero-curator-row" className="mt-5 pt-4 border-t border-white/20 flex flex-wrap items-center gap-x-5 gap-y-1.5">
                  <span className="text-[10px] uppercase tracking-wider text-white/50">Your Curator</span>
                  {curatorName && (
                    <span className="inline-flex items-center gap-1.5 text-white text-sm font-medium"><User className="h-3.5 w-3.5" />{curatorName}</span>
                  )}
                  {curatorPhone && (
                    <span className="inline-flex items-center gap-1.5 text-white/80 text-xs"><Phone className="h-3.5 w-3.5" />{curatorPhone}</span>
                  )}
                  {curatorEmail && (
                    <span className="inline-flex items-center gap-1.5 text-white/80 text-xs"><Mail className="h-3.5 w-3.5" />{curatorEmail}</span>
                  )}
                </div>
              )}
            </div>
          </div>
          ) : (
          <div
            data-testid="itinerary-hero"
            className="relative w-full overflow-hidden flex flex-col justify-between"
            style={{
              minHeight: '620px',
              ...(hasHeroPhoto
                ? { backgroundImage: `url(${backgroundImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                : { background: 'radial-gradient(ellipse at top, #0F766E 0%, #134E4A 48%, #042F2E 100%)' }),
            }}
          >
            {/* 3-layer scrim (design_agent brief) — guarantees a plain white
                logo + white text always reads regardless of source-photo
                brightness, no per-image detection needed. */}
            {hasHeroPhoto ? (
              <>
                <div className="absolute inset-0" style={{ background: 'rgba(4,47,46,0.32)', mixBlendMode: 'multiply' }} />
                <div className="absolute inset-x-0 top-0 h-48" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.65), transparent)' }} />
                <div className="absolute inset-x-0 bottom-0" style={{ height: '62%', background: 'linear-gradient(to top, rgba(0,0,0,0.94), rgba(0,0,0,0.55) 45%, transparent)' }} />
              </>
            ) : (
              <div
                className="absolute inset-0"
                style={{
                  opacity: 0.18,
                  backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=%27http://www.w3.org/2000/svg%27 width=%27120%27 height=%27120%27><filter id=%27n%27><feTurbulence type=%27fractalNoise%27 baseFrequency=%270.85%27 numOctaves=%272%27 stitchTiles=%27stitch%27/></filter><rect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27 opacity=%270.4%27/></svg>")',
                }}
              />
            )}

            {/* Top row — logo (pure white, no plate) + curator glass pill */}
            <div className="relative z-20 flex items-start justify-between px-6 md:px-10 pt-7 md:pt-9">
              <img
                src={logoUrl}
                alt={companyName}
                data-testid="hero-logo"
                className="h-8 md:h-10 w-auto object-contain"
                style={{ filter: 'brightness(0) invert(1)', opacity: 0.95 }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              {(curatorName || curatorPhone || curatorEmail) && (
                <div
                  data-testid="hero-curator-pill"
                  className="hidden sm:flex items-center gap-3 rounded-full pl-1.5 pr-4 py-1.5"
                  style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', backdropFilter: 'blur(12px)' }}
                >
                  <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.15)' }}>
                    <User className="h-3.5 w-3.5 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="text-[10px] uppercase tracking-wider text-white/50 leading-none">Your Curator</p>
                    <p className="text-xs font-medium text-white leading-tight mt-0.5">{curatorName || curatorPhone || curatorEmail}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Center content, bottom-anchored — editorial/cinematic composition */}
            <div className="relative z-20 flex flex-col items-center text-center px-6 pb-16 md:pb-20 mt-auto">
              <p
                data-testid="hero-accent-line"
                className="text-2xl md:text-4xl mb-2 md:mb-3"
                style={{ fontFamily: "'La Belle Aurore', cursive", color: '#FCD34D' }}
              >
                Your {destinationFirstWord} Escape
              </p>
              <h1
                data-testid="hero-destination-title"
                className="text-white tracking-tight leading-none drop-shadow-2xl"
                style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(2.5rem, 7vw, 5.5rem)', wordBreak: 'break-word', maxWidth: '100%' }}
              >
                {destination}
              </h1>
              {caption && (
                <p className="italic text-white/75 text-base md:text-lg font-light mt-4 max-w-xl">&ldquo;{caption}&rdquo;</p>
              )}

              <div data-testid="hero-meta-row" className="flex flex-wrap items-center justify-center gap-2.5 md:gap-3 mt-7">
                {heroDateRange && (
                  <span data-testid="hero-meta-chip-dates" className="hero-meta-chip"><Calendar className="h-3.5 w-3.5" />{heroDateRange}</span>
                )}
                {displayTripId && (
                  <span data-testid="hero-meta-chip-tripid" className="hero-meta-chip"><Hash className="h-3.5 w-3.5" />{displayTripId}</span>
                )}
                <span data-testid="hero-meta-chip-pax" className="hero-meta-chip">
                  <Users className="h-3.5 w-3.5" />
                  {totalPax} Traveller{totalPax === 1 ? '' : 's'}
                </span>
              </div>

              {/* Curator on mobile (the top-right glass pill is sm:+ only) */}
              {(curatorName || curatorPhone || curatorEmail) && (
                <div data-testid="hero-curator-row-mobile" className="sm:hidden flex flex-wrap items-center justify-center gap-x-4 gap-y-1 mt-5 pt-4 border-t border-white/15 text-white/80 text-xs">
                  {curatorName && <span className="inline-flex items-center gap-1.5"><User className="h-3 w-3" />{curatorName}</span>}
                  {curatorPhone && <span className="inline-flex items-center gap-1.5"><Phone className="h-3 w-3" />{curatorPhone}</span>}
                </div>
              )}
            </div>

            {/* Scroll cue — Item 6 fix: this was a purely decorative div
                (bounce animation + "Explore" label) with no click handler,
                so tapping/clicking it did nothing despite looking like an
                affordance. Now a real button that smooth-scrolls to the
                content just below the hero. */}
            <button
              type="button"
              onClick={() => document.getElementById('below-hero-content')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="hidden md:flex absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex-col items-center gap-1 text-white/60 hover:text-white/90 transition-colors cursor-pointer bg-transparent border-none"
              data-testid="hero-scroll-cue"
              aria-label="Scroll to itinerary details"
            >
              <span className="text-[10px] uppercase tracking-[0.2em]">Explore</span>
              <ChevronDown className="h-4 w-4 animate-bounce" />
            </button>

            <style>{`
              [data-testid="itinerary-hero"] .hero-meta-chip {
                display: inline-flex; align-items: center; gap: 7px;
                backdrop-filter: blur(12px);
                background: rgba(255,255,255,0.1);
                border: 1px solid rgba(255,255,255,0.2);
                color: #ffffff;
                padding: 9px 18px;
                border-radius: 999px;
                font-size: 13px;
                font-weight: 500;
                letter-spacing: 0.01em;
                transition: background-color 0.2s;
              }
              [data-testid="itinerary-hero"] .hero-meta-chip:hover { background: rgba(255,255,255,0.18); }
            `}</style>
          </div>
          )}
          
          {/* BELOW HERO: Greeting + Quote + Notes.
              Trip ID / dates / traveller count / curator now live in the
              hero above — no longer duplicated here. */}
          <div className="flex-1 bg-white px-8 py-4 relative" id="below-hero-content">
            {/* Subtle Watermarks */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-[0.03]">
              <Plane className="absolute top-4 right-12 h-16 w-16 rotate-45 text-slate-400" />
              <Globe className="absolute bottom-8 right-1/4 h-14 w-14 text-slate-400" />
            </div>
            
            <div className="relative z-10 max-w-3xl mx-auto">
              <GreetingDatesRow guestName={guestName} duration={duration} destination={destination} />

              {/* Single-quote lump-sum pricing, right below the greeting
                  (1b — was "single-quote pricing (no hotel options)"; the
                  2-option comparison + page-2 pricing block are retired).
                  Corrections session — on the LIVE page this is now the
                  ONLY place price+payment showed besides the sticky panel,
                  so it's retired from the live view entirely and kept
                  PRINT-ONLY (PDF parity deferred to Session 5). Live price
                  now lives solely in <StickyPricePanel/> below. */}
              {isPrintMode && showPrice && coverEntries.length > 0 && (
                <div className="mt-2 mb-2" id="pricing" data-testid="cover-pricing">
                  <PricingSection
                    itinerary={itinerary}
                    optionEntries={coverEntries}
                    isPrintMode={isPrintMode}
                    paxMode={paxMode}
                    setPaxMode={setPaxMode}
                    unboxed
                  />
                </div>
              )}

              <TripOptionsCompareSection options={tripPricingOptions} />

              {/* Quote & Pricing Section (print-only, see note above)
                  Phase B2 FEATURE 3 — hidden when "Include price" is OFF. */}
              {isPrintMode && showPrice && totalCost > 0 && coverEntries.length === 0 && (
                <div className="price-display mb-6" data-testid="quote-pricing-section">
                  <div className="flex flex-col items-start gap-1">
                    <p style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--color-text-tertiary, #94a3b8)', fontWeight: 600 }}>Package Cost</p>
                    <div className="flex items-center gap-1">
                      <span className="text-3xl font-bold text-slate-900">
                        ₹{formatPrice(totalCost)}
                      </span>
                    </div>
                    <p style={{ fontSize: '13px', fontWeight: 500, color: 'var(--color-text-secondary, #475569)' }}>
                      {quoteBasis || 'Per Person'}
                    </p>
                    <p style={{ fontSize: '11px', fontStyle: 'italic', color: 'var(--color-text-tertiary, #94a3b8)' }}>
                      * Inclusive of GST
                    </p>
                  </div>
                  {/* Phase B2 FEATURE 4 — Book Now / Pay Now CTA. Sits
                      inside the price-display block so it inherits the
                      `showPrice` visibility (the link is hidden when the
                      "Include price" toggle is OFF, by design). Renders as
                      a real <a href> hyperlink so Playwright's PDF carries
                      it as a clickable link. */}
                  {paymentLink && (
                    <div
                      style={{ textAlign: 'center', marginTop: '14px' }}
                      data-testid="itin-pay-now-block"
                    >
                      <a
                        href={paymentLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-block',
                          padding: '10px 28px',
                          background: '#5BC4CD',
                          color: '#4A585F',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: 600,
                          textDecoration: 'none',
                        }}
                        data-testid="itin-pay-now-btn"
                      >
                        Pay Now
                        {paymentAmount && Number(paymentAmount) > 0
                          ? ` — ₹${formatPrice(paymentAmount)}`
                          : ''}
                      </a>
                      <div style={{ marginTop: '6px', fontSize: '11px', color: '#9ca3af' }}>
                        Secure payment · Instant confirmation
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Quote Notes */}
              {quoteNotes && quoteNotes.length > 0 && (
                <div className="mb-6">
                  <ul className="space-y-1.5">
                    {quoteNotes.map((note, idx) => (
                      <li key={`note-${idx}-${note.slice(0, 10)}`} className="flex items-start gap-2 text-sm text-slate-600">
                        <span className="text-amber-500 mt-0.5 font-bold">*</span>
                        <span>{note}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Trip ID / dates / traveller count / curator contact now
                  live overlaid on the hero photo above (Session 1 redesign). */}

              {/* FIX 5 — Section jump nav at the bottom of page 1 (hidden in PDF) */}
              {!isPrintMode && <div className="hidden md:block"><SectionJumpNav sections={navSections} /></div>}
            </div>
            
            {/* Company Logo at Bottom Right of cover — kept low-opacity and
                pinned to the lower-right corner so the curator block above
                doesn't overlap. Preserves aspect ratio (no stretching).
                Phase B1 FIX 5: hidden in PDF (printMode) — the brand logo
                is now stamped on every page via the Playwright footer. */}
            {!printMode && (
            <div className="absolute bottom-3 right-4 opacity-40 z-0 pointer-events-none">
              <img
                src={logoUrl}
                alt={companyName}
                className="h-9 w-auto"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
            )}
          </div>
        </div>

        {/* About Us + Destinations relocated to just above the footer (below). */}

        {/* ===== PAGE 3+: CONTENT SECTIONS ===== */}
        {/* Session 3a correction pass — ivory bg is now the CONFIRMED brand
            hex (#FAFAF8, travcations.com --ivory), not an invented tone.
            Top border also switched to the confirmed brand teal (#158A68).
            Inner section cards keep explicit bg-white so they read as
            "paper on paper" against this tone. SVG noise is a static
            data-URI image (no blur/filter), PDF-safe. */}
        <div
          className="border-t-4 border-[#158A68] bg-[#FAFAF8] bg-[url('data:image/svg+xml;utf8,%3Csvg%20viewBox%3D%220%200%20200%20200%22%20xmlns%3D%22http://www.w3.org/2000/svg%22%3E%3Cfilter%20id%3D%22n%22%3E%3CfeTurbulence%20type%3D%22fractalNoise%22%20baseFrequency%3D%220.8%22%20numOctaves%3D%223%22%20stitchTiles%3D%22stitch%22/%3E%3C/filter%3E%3Crect%20width%3D%22100%25%22%20height%3D%22100%25%22%20filter%3D%22url(%23n)%22%20opacity%3D%220.04%22/%3E%3C/svg%3E')]"
          data-testid="itinerary-body-background"
        >

          {/* ===== HOTEL DETAILS SECTION ===== */}
          {/* Phase 5 fix Issue 1: removed `pdf-no-break` from this <section>
              so individual HotelCard children control their own page-break
              behaviour (otherwise the whole section was treated as one
              unbreakable block, forcing the 2nd hotel + everything after
              to a new page and leaving Page 3 blank). */}
          {hotels && hotels.length > 0 && (
            <section id="accommodation" className="px-8 md:px-12 py-12 border-b border-slate-100 relative">
              {/* Session 3a corrections pass — removed the generic 2%-opacity
                  Hotel/MapPin lucide-icon watermark (flagged as a low-quality
                  "AI-style print"). Replaced with ONE sparse line-art compass
                  accent in the confirmed brand teal — a single deliberate
                  mark, not a repeating pattern. Proposed here as the direction
                  to approve before applying to any other section. */}
              <CompassAccent />

              <div className="relative z-10">
                <SectionHeader 
                  icon={<Hotel className="h-6 w-6" />}
                  title="Accommodation"
                  subtitle="Your Stay Details"
                  number="01"
                />
                
                <div className="mt-8">
                  {regularStays.map((hotel, idx) => (
                    <HotelCard key={`hotel-${idx}-${hotel.hotelName || ''}`} hotel={hotel} index={idx} />
                  ))}

                  {hotelDisclaimer && (
                    <div className="mt-6 p-4 bg-amber-50 border-l-4 border-amber-400 rounded-r-xl">
                      <div className="text-sm text-amber-800 flex items-start gap-2">
                        <StickyNote className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <strong className="block mb-1">Note:</strong>
                          {hotelDisclaimer.includes('<')
                            ? <div className="prose prose-sm max-w-none text-amber-800" dangerouslySetInnerHTML={{ __html: hotelDisclaimer }} />
                            : <span>{hotelDisclaimer}</span>}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* ===== DAY-WISE ITINERARY SECTION ===== */}
          {/* Phase 5 fix Issue 1/9 follow-up: removed `pdf-no-break` from
              this <section> so it can share a page with the preceding
              section (Accommodation) — eliminates the page-3 blank.
              Per-card protection lives on DayCard via breakInside: avoid. */}
          {days && days.length > 0 && (
            <section id="itinerary" className="px-8 md:px-12 py-12 bg-gradient-to-b from-slate-50/50 to-white border-b border-slate-100 relative">
              {/* Watermarks */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-[0.02]">
                <Calendar className="absolute top-12 left-8 h-16 w-16 text-slate-400" />
                <Plane className="absolute bottom-16 right-12 h-20 w-20 rotate-45 text-slate-400" />
              </div>
              
              <div className="relative z-10">
                <SectionHeader 
                  icon={<Calendar className="h-6 w-6" />}
                  title="Your Journey"
                  subtitle="Day by Day Itinerary"
                  number="02"
                />
                {!isPrintMode && <RouteMapStrip groups={destGroups} />}
                {days.length > 1 && <MobileDayNav count={days.length} active={mobileDay} onSelect={setMobileDay} />}

                {/* Phase B2 FEATURE 2 — Trip Highlights strip (only renders
                    when the itinerary has at least one highlight). Sits
                    between the section header and Day 1. */}
                {Array.isArray(highlights) && highlights.length > 0 && (
                  <div
                    data-testid="itin-highlights-strip"
                    style={{
                      background: 'linear-gradient(135deg, #f0fdf4, #f0f9ff)',
                      border: '1px solid #bfdbfe',
                      borderRadius: '8px',
                      padding: '16px 20px',
                      marginTop: '24px',
                      marginBottom: '24px',
                      pageBreakInside: 'avoid',
                      breakInside: 'avoid',
                    }}
                  >
                    <div style={{
                      fontSize: '10px', fontWeight: 600, letterSpacing: '0.16em',
                      textTransform: 'uppercase', color: '#0891b2', marginBottom: '12px',
                    }}>
                      Trip Highlights
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {highlights.map((h, i) => (
                        <span
                          key={i}
                          style={{
                            padding: '4px 14px',
                            background: '#ffffff',
                            border: '1px solid #bfdbfe',
                            borderRadius: '20px',
                            fontSize: '12px',
                            color: '#1e40af',
                            fontWeight: 500,
                          }}
                        >
                          ✦ {h}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Mobile: true tab switching — only the selected day is shown */}
                <div className="md:hidden mt-2" data-testid="m-day-panel-wrap">
                  <MobileDayPanel day={days[Math.min(mobileDay, days.length) - 1] || days[0]} />
                </div>
                {/* Desktop — PDF (Playwright, ?print=1) keeps the original
                    always-expanded timeline unchanged pending Session 5's
                    PDF-parity pass. Live preview gets the Session 2
                    destination-banner-grouped, collapsible day cards. */}
                <div className="hidden md:block mt-8 space-y-0">
                  {isPrintMode ? (
                    days.map((day, idx) => (
                      <div key={`day-${day.dayNumber || idx}`} id={`day-${idx + 1}`} data-day={idx + 1} style={{ scrollMarginTop: '104px' }}>
                        <DayCard day={day} isLast={idx === days.length - 1} />
                      </div>
                    ))
                  ) : (
                    destGroups.map((group, gi) => (
                      <div
                        key={`day-group-${gi}`}
                        data-testid={`day-group-${gi}`}
                        className="mb-8 rounded-xl border border-slate-200 overflow-hidden bg-white"
                      >
                        {showDestBanners && <DestinationBanner group={group} index={gi} destination={destination} destinationPhotoOverrides={destinationPhotoOverrides} />}
                        <div className="px-4 md:px-5">
                          {group.days.map(({ day }, di) => {
                            const globalIdx = destGroups.slice(0, gi).reduce((acc, g) => acc + g.days.length, 0) + di;
                            const dayDate = travelStartDate ? addDaysToDate(travelStartDate, globalIdx) : null;
                            return (
                              <div key={`day-${day.dayNumber || globalIdx}`} id={`day-${globalIdx + 1}`} data-day={globalIdx + 1} style={{ scrollMarginTop: '104px' }}>
                                <CollapsibleDayCard
                                  day={day}
                                  dayDate={dayDate}
                                  isLast={di === group.days.length - 1}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </section>
          )}

          {/* ===== INCLUSIONS & EXCLUSIONS SECTION ===== */}
          {/* Phase B1 FIX 1: removed `pdf-no-break` so this section can
              share a page with the tail of Days (e.g. Day 7 + Package
              Details on the same page). Session 3a: "Editorial Ledger"
              restyle — single premium container with a central divider
              instead of two colored blocky cards. */}
          {(inclusions?.length > 0 || exclusions?.length > 0) && (
            <section id="inclusions" className="px-8 md:px-12 py-12 border-b border-slate-100 relative">
              {/* Watermarks */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-[0.02]">
                <CheckCircle2 className="absolute top-8 right-20 h-16 w-16 text-slate-400" />
                <XCircle className="absolute bottom-12 left-16 h-14 w-14 text-slate-400" />
              </div>
              
              <div className="relative z-10">
                <SectionHeader 
                  icon={<CheckCircle2 className="h-6 w-6" />}
                  title="Package Details"
                  subtitle="What's Included & Not Included"
                  number="03"
                />

                {/* Print/PDF fix (2026-08-31) — `pdf-no-break` now scopes ONLY
                    to this card (not the whole section incl. the heading
                    above). Previously the heading was dragged into the same
                    atomic block as this often-tall 2-column ledger, so
                    whenever the ledger didn't fit in the space left on the
                    current page, the WHOLE section (heading included) jumped
                    to a fresh page — leaving the previous page's remainder
                    blank. Now the heading can render wherever there's room
                    and only the ledger card itself avoids splitting. */}
                <div className="relative bg-white border border-slate-200 rounded-2xl p-6 md:p-10 lg:p-12 mt-8 pdf-no-break" data-testid="package-details-ledger">
                  <div className={`grid ${inclusions?.length > 0 && exclusions?.length > 0 ? 'grid-cols-1 md:grid-cols-2 md:after:content-[\'\'] md:after:absolute md:after:top-0 md:after:bottom-0 md:after:left-1/2 md:after:-translate-x-1/2 md:after:w-px md:after:bg-slate-100' : 'grid-cols-1'} gap-10 md:gap-16 lg:gap-24 relative`}>
                    {/* Inclusions */}
                    {inclusions?.length > 0 && (
                      <div data-testid="inclusions-column">
                        <div className="flex items-center gap-4 mb-8">
                          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-emerald-50 text-emerald-600">
                            <CheckCircle2 className="h-5 w-5" />
                          </div>
                          <h3 className="font-heading text-2xl text-slate-900">Inclusions</h3>
                        </div>
                        <ul className="space-y-[18px]">
                          {inclusions.map((item, idx) => (
                            <li key={`inc-${idx}-${item.slice(0, 10)}`} className="flex items-start gap-3.5 group" data-testid={`inclusion-item-${idx}`}>
                              <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0 transition-transform group-hover:scale-110" />
                              <span className="text-sm text-slate-700 leading-relaxed">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Exclusions */}
                    {exclusions?.length > 0 && (
                      <div data-testid="exclusions-column">
                        <div className="flex items-center gap-4 mb-8">
                          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-rose-50 text-rose-500">
                            <XCircle className="h-5 w-5" />
                          </div>
                          <h3 className="font-heading text-2xl text-slate-900">Exclusions</h3>
                        </div>
                        <ul className="space-y-[18px]">
                          {exclusions.map((item, idx) => (
                            <li key={`exc-${idx}-${item.slice(0, 10)}`} className="flex items-start gap-3.5 group" data-testid={`exclusion-item-${idx}`}>
                              <XCircle className="w-5 h-5 text-rose-400 mt-0.5 shrink-0 transition-transform group-hover:scale-110" />
                              <span className="text-sm text-slate-500 leading-relaxed group-hover:text-slate-600 transition-colors">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* ===== CRUISE PACKAGE INCLUSIONS / EXCLUSIONS (Correction 3) ===== */}
          {hasCruise && (
            <CruiseIncExcSection inclusions={cruise_inclusions} exclusions={cruise_exclusions} />
          )}

          {/* ===== FLIGHT DETAILS SECTION ===== */}
          {/* Phase 5 fix Issue 9: removed `pdf-no-break` from this <section>
              (same root cause as Issue 1 hotels) so Playwright can break the
              page naturally instead of pushing the whole flight section to
              the next page and leaving page 7 short. The inner flight card
              already has no `pdf-no-break` — small enough to flow. */}
          <section id="flights" className="section-flights px-8 md:px-12 py-12 border-b border-slate-100 relative">
            <div className="relative z-10">
              <SectionHeader 
                icon={<Plane className="h-6 w-6" />}
                title="Flight Information"
                subtitle="Your Air Travel Details"
                number="04"
              />
              
              {flightVouchers.length > 0 ? (
                /* Structured flight cards (with airline logos) sourced from
                   the trip's flight vouchers. Session 3b: dense row wrapper
                   matching the Accommodation list-row language + confirmed
                   primary palette (no more sky/blue). */
                <div className="mt-8 space-y-3" data-testid="itinerary-flight-structured">
                  {sortedFlightVouchers.map((v, vi) => (
                    <div key={v.voucherId || vi} className="bg-white p-4 border border-[#052E22]/15 rounded-sm pdf-no-break">
                      <ItineraryFlightCard
                        flight={v.flight}
                        company={companySettings}
                        passengers={v.flight?.passengers || []}
                      />
                    </div>
                  ))}
                </div>
              ) : gdsDirections.length > 0 ? (
                /* No structured voucher — render boarding-pass cards parsed
                   from the free-text GDS blob (one per direction). */
                <GdsFlightList directions={gdsDirections} />
              ) : (
                <div className="mt-8 bg-white p-4 border border-[#052E22]/15 rounded-sm pdf-no-break">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 shrink-0 rounded-sm flex items-center justify-center" style={{ background: '#0A5C48' }}>
                      <Plane className="h-5 w-5 text-white" />
                    </div>
                    <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">
                      {flightDetails && flightDetails.trim() ? flightDetails : (
                        <span className="text-slate-400 italic">Not Applicable - Flight details will be updated once confirmed.</span>
                      )}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* ===== TERMS & CONDITIONS SECTION ===== */}
          {/* Phase 5 fix Issue 1/9 follow-up: removed `pdf-no-break` so the
              T&C section can share a page with the preceding Flight Info —
              eliminates the page-7 blank below the flight card. */}
          {/* Session 4 — retired the generic 2%-opacity ScrollText/CreditCard
              lucide watermark + slate-50/violet gradient blocks (same
              low-quality "AI-style print" flagged in Session 3a). Both
              sections now share the ledger-card language from Package
              Details (white card, slate-200/[#052E22]-15 border, rounded-2xl)
              with a thin PRIMARY-palette accent bar on top — teal for Terms,
              gold for Payment — so the two stay visually distinct at a glance. */}
          {termsAndConditions && (
            <section id="terms" className="section-terms px-8 md:px-12 py-12 border-b border-slate-100 relative">
              <div className="relative z-10">
                <SectionHeader 
                  icon={<ScrollText className="h-6 w-6" />}
                  title="Terms & Conditions"
                  subtitle="Important Information"
                  number="05"
                />
                
                <div className="mt-8 relative bg-white rounded-2xl p-6 md:p-10 border border-[#052E22]/15 overflow-hidden pdf-no-break" data-testid="terms-ledger">
                  <div className="absolute top-0 left-0 right-0 h-1" style={{ background: '#158A68' }} />
                  <RichDisplay html={termsAndConditions} className="text-sm text-slate-700 leading-relaxed" />
                </div>
              </div>
            </section>
          )}

          {/* ===== PAYMENT & CANCELLATION POLICY SECTION ===== */}
          {paymentPolicy && (
            <section id="payment" className="px-8 md:px-12 py-12 border-b border-slate-100 relative pdf-no-break">
              <div className="relative z-10">
                <SectionHeader 
                  icon={<CreditCard className="h-6 w-6" />}
                  title="Payment & Cancellation"
                  subtitle="Booking & Refund Policy"
                  number="06"
                />
                
                <div className="mt-8 relative bg-white rounded-2xl p-6 md:p-10 border border-[#052E22]/15 overflow-hidden" data-testid="payment-policy-ledger">
                  <div className="absolute top-0 left-0 right-0 h-1" style={{ background: '#C9973A' }} />
                  <RichDisplay html={paymentPolicy} className="text-sm text-slate-700 leading-relaxed" />
                </div>
              </div>
            </section>
          )}

          {/* ===== NOTES SECTION (Internal - hidden from clients & PDFs) ===== */}
          {notes && !clientFacing && (
            <section id="notes" className="px-8 md:px-12 py-12 border-b border-slate-100 pdf-no-break">
              <SectionHeader 
                icon={<FileText className="h-6 w-6" />}
                title="Additional Notes"
                subtitle="Things to Remember"
                number="07"
              />
              
              <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
                <RichDisplay html={notes} className="text-amber-900" />
              </div>
            </section>
          )}

          {/* ===== PAYMENT DETAILS SECTION (Internal - hidden from clients & PDFs) ===== */}
          {paymentDetails && !clientFacing && (
            <section className="px-8 md:px-12 py-12 border-b border-slate-100 pdf-no-break">
              <SectionHeader 
                icon={<CreditCard className="h-6 w-6" />}
                title="Payment Details"
                subtitle="Bank & Payment Information"
                number="08"
              />
              
              <div className="mt-8 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 border border-emerald-200">
                <RichDisplay html={paymentDetails} />
              </div>
            </section>
          )}
        </div>

        {/* C4 — Optional Add-ons & Supplement Costs (after notes, before payment).
            Task 6 — hidden entirely when the price toggle is OFF, so no ₹ leaks
            on the preview or the PDF. */}
        {showPrice && (
          <SupplementsSection supplements={itinerary.supplement_costs} isPrintMode={isPrintMode} />
        )}

        {/* C3 — Payment link + WhatsApp/Email (hidden in PDF) */}
        {/* Agent-only payment toolkit (Generate link / Copy / WhatsApp / Email).
            Hidden from client itinerary views, client payment pages, PDF exports
            and print mode — clients only ever see the per-option "Book Now" CTA. */}
        {!printMode && !isPrintMode && !clientMode && <PaymentSection itinerary={itinerary} />}

        {/* ===== ABOUT US + DESTINATIONS (moved here — just above the footer) =====
            About the agency on top; "Destinations We Specialise In" renders
            ONLY when destination logos are configured (no placeholder text). */}
        <section
          data-testid="itin-about-us-section"
          className="px-8 md:px-12 py-12 border-t border-slate-100 pdf-no-break"
          style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '32px', alignItems: 'flex-start' }}>
          {/* About Us */}
          <div data-testid="itin-about-us-top" style={{ flex: '1 1 320px', minWidth: 0 }}>
            <div style={{
              fontSize: '10px', fontWeight: 600, letterSpacing: '0.08em',
              textTransform: 'uppercase', color: 'var(--color-text-tertiary, #94a3b8)', marginBottom: '12px',
            }}>
              About {companySettings?.companyName || 'Travcations'}
            </div>
            <div style={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontSize: '22px', fontWeight: 600, color: '#111827',
              marginBottom: '12px', lineHeight: 1.2,
            }}>
              {companySettings?.aboutTagline || "Let's Explore to Experience"}
            </div>
            <p style={{ fontSize: '13px', color: '#6b7280', lineHeight: 1.75, margin: 0 }}>
              {companySettings?.aboutText || (
                'Travcations LLP is a premium travel agency based in Kolkata, '
                + 'crafting extraordinary journeys for discerning travellers '
                + 'since 2012. From Norwegian fjords to Balinese temples — we '
                + 'design every trip with precision, passion, and personal care.'
              )}
            </p>
            {(companySettings?.established || companySettings?.teamSize) && (
              <div style={{ marginTop: '16px', fontSize: '12px', color: '#9ca3af' }}>
                {companySettings?.established && `Est. ${companySettings.established}`}
                {companySettings?.established && companySettings?.teamSize && ' · '}
                {companySettings?.teamSize && `${companySettings.teamSize} travel experts`}
              </div>
            )}
          </div>

          {/* Destinations — render the whole block ONLY when logos exist.
              Sits to the RIGHT of About Us with a thin vertical section break
              (no hard box). */}
          {Array.isArray(companySettings?.destinationLogos) && companySettings.destinationLogos.length > 0 && (
            <div
              data-testid="itin-about-us-bottom"
              style={{ flex: '1 1 280px', minWidth: 0, borderLeft: '1px solid #e5e7eb', paddingLeft: '32px' }}
            >
              <div style={{
                fontSize: '10px', fontWeight: 600, letterSpacing: '0.08em',
                textTransform: 'uppercase', color: 'var(--color-text-tertiary, #94a3b8)', marginBottom: '12px',
              }}>
                Destinations We Specialise In
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-start' }}>
                {companySettings.destinationLogos.slice(0, 15).map((logo, idx) => (
                  <div key={idx} style={{ textAlign: 'center', width: 'calc(33.333% - 8px)' }}>
                    <img
                      src={logo.url}
                      alt={logo.name || ''}
                      style={{ height: '28px', width: 'auto', maxWidth: '100%', display: 'block', margin: '0 auto 4px' }}
                    />
                    <div style={{ fontSize: '8px', color: '#9ca3af', lineHeight: 1.2 }}>
                      {logo.name}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          </div>
        </section>

        {/* ===== FOOTER (last page) — clean white, full-width, generous padding =====
            FIX 5: left = logo + tagline · right = website (+ phone/email) ·
            centred "Crafted by {company}" (11px tertiary, no heart emoji). */}
        <div className="bg-white border-t-2 border-amber-300 mt-3 px-6 md:px-12 py-14 pdf-no-break" data-testid="last-page-footer">
          <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-start justify-between gap-6">
            {/* Left — logo + tagline */}
            <div className="shrink-0">
              <img
                src={logoUrl}
                alt={companyName}
                style={{ height: '56px', width: 'auto' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div style={{display: 'none'}} className="w-14 h-14 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center shadow-lg">
                <Globe className="h-7 w-7 text-white" />
              </div>
              <p style={{ marginTop: '10px', fontSize: '12px', color: 'var(--color-text-tertiary, #94a3b8)' }} data-testid="footer-tagline">
                {companySettings?.aboutTagline || "Let's Explore to Experience"}
              </p>
            </div>

            {/* Right — website URL (primary) + phone/email */}
            <div className="flex flex-col items-start md:items-end gap-2 text-sm text-slate-600">
              {companySettings?.website && (
                <a href={companySettings.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-amber-600 transition-colors font-medium" data-testid="footer-website">
                  <Globe className="h-4 w-4 text-teal-600" /> {companySettings.website}
                </a>
              )}
              {companySettings?.phone && (
                <a href={`tel:${companySettings.phone}`} className="flex items-center gap-2 hover:text-amber-600 transition-colors">
                  <Phone className="h-4 w-4 text-teal-600" /> {companySettings.phone}
                </a>
              )}
              {companySettings?.email && (
                <a href={`mailto:${companySettings.email}`} className="flex items-center gap-2 hover:text-amber-600 transition-colors">
                  <Mail className="h-4 w-4 text-teal-600" /> {companySettings.email}
                </a>
              )}
            </div>
          </div>

          {/* Centre bottom — single credit line, no heart emoji */}
          <div className="max-w-5xl mx-auto mt-8 pt-6 border-t border-slate-100 text-center">
            <p style={{ fontSize: '11px', color: 'var(--color-text-tertiary, #94a3b8)' }} data-testid="footer-credit">
              Crafted by {companyName}
            </p>
          </div>
        </div>
      </div>

      {/* Corrections session (2026-08-27) — consolidated sticky price panel.
          Replaces the old client-only "Download PDF / Pay Now" bottom bar
          AND the two inline price blocks above (cover-pricing /
          quote-pricing-section, now print-only) — price + the payment CTA
          appear in exactly ONE place on the live page. Visible in both the
          agent's own preview and the client route (matches the old
          cover-pricing visibility), hidden only during actual PDF capture. */}
      {!isPrintMode && showPrice && (
        <StickyPricePanel
          itinerary={itinerary}
          coverEntries={coverEntries}
          onWhatsAppShare={handleWhatsAppShare}
          onDownload={() => handleDownloadPDF('compare')}
          downloading={pdfLoading}
        />
      )}
    </div>
  );
}

// ===== REUSABLE COMPONENTS =====

// FIX 5 — horizontal scrollable pill nav that smooth-scrolls to a section.
// Mobile-only (md:hidden) sticky section pill strip. Teal-filled active pill,
// bordered inactive. Scroll-spy via IntersectionObserver; tap → smooth scroll.
const MobileSectionNav = ({ sections }) => {
  const [active, setActive] = useState(sections[0]?.id || '');
  useEffect(() => {
    if (!sections.length) return undefined;
    const obs = new IntersectionObserver((entries) => {
      const visible = entries.filter((e) => e.isIntersecting);
      if (visible.length) {
        visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        setActive(visible[0].target.id);
      }
    }, { rootMargin: '-45% 0px -50% 0px', threshold: 0 });
    sections.forEach((s) => { const el = document.getElementById(s.id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [sections]);
  const jump = (id) => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); };
  return (
    <div className="md:hidden sticky top-0 z-30 bg-white border-b border-slate-100" data-testid="m-itin-subtabs">
      <div className="flex gap-2 px-3 py-2 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
        {sections.map((s) => {
          const on = active === s.id;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => jump(s.id)}
              data-testid={`m-subtab-${s.id}`}
              className="shrink-0 whitespace-nowrap rounded-full border font-medium transition-colors"
              style={{
                height: '36px', padding: '6px 14px', fontSize: '13px',
                background: on ? '#0F6E56' : '#ffffff',
                color: on ? '#ffffff' : '#475569',
                borderColor: on ? '#0F6E56' : '#E2E8F0',
              }}
            >
              {s.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Parse a day's activities/meals field (stored as a JSON string or array).
const parseDayList = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v === 'string') {
    const t = v.trim();
    if (!t) return [];
    try { const p = JSON.parse(t); return Array.isArray(p) ? p : (p ? [p] : []); } catch { return [t]; }
  }
  return [];
};

// Mobile-only (md:hidden) day strip — DAY 1 | DAY 2 … horizontal scroll, teal
// underline on the active day. TRUE TAB SWITCHING (controlled): tapping a day
// shows only that day's panel (see MobileDayPanel).
const MobileDayNav = ({ count, active, onSelect }) => {
  const items = [];
  for (let i = 1; i <= count; i += 1) items.push(i);
  return (
    <div className="md:hidden flex gap-5 overflow-x-auto border-b border-slate-200 mt-4 mb-2" style={{ scrollbarWidth: 'none' }} data-testid="m-day-nav">
      {items.map((n) => {
        const on = active === n;
        return (
          <button
            key={n}
            type="button"
            onClick={() => onSelect(n)}
            data-testid={`m-day-tab-${n}`}
            className="shrink-0 whitespace-nowrap pb-2 transition-colors"
            style={{
              fontSize: '13px', fontWeight: 600,
              color: on ? '#0F6E56' : '#94a3b8',
              borderBottom: on ? '2px solid #0F6E56' : '2px solid transparent',
            }}
          >
            DAY {n}
          </button>
        );
      })}
    </div>
  );
};

// Mobile-only single-day panel — clean format (no timeline circle / "DAY X"
// label). Shows: title + description, activities (time + name), hotel that
// night, meals included.
const MobileDayPanel = ({ day }) => {
  if (!day) return null;
  const activities = parseDayList(day.activities);
  const meals = [...parseDayList(day.meals), ...parseDayList(day.mealPlan)];
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="m-day-panel">
      {day.title ? (
        <h3 className="font-heading text-[17px] font-bold text-slate-900" style={{ wordBreak: 'break-word' }}>{day.title}</h3>
      ) : null}
      {day.description ? (
        <div className="mt-2 text-[13px] text-slate-600" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
          <DayDescription text={day.description} />
        </div>
      ) : null}
      {day.imageUrl ? (
        <img
          src={day.imageUrl.startsWith('/') ? `${BACKEND_URL}${day.imageUrl}` : day.imageUrl}
          alt={day.title || `Day ${day.dayNumber}`}
          loading="lazy"
          className="mt-3 w-full h-44 object-cover rounded-xl"
        />
      ) : null}

      {activities.length > 0 && (
        <div className="mt-4" data-testid="m-day-activities">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-teal-600 mb-2">Activities</p>
          <ul className="space-y-2">
            {activities.map((a, i) => {
              const time = typeof a === 'object' && a ? (a.time || a.startTime || '') : '';
              const name = typeof a === 'object' && a ? (a.activity || a.title || a.name || a.description || '') : String(a);
              if (!name) return null;
              return (
                <li key={i} className="flex gap-2 text-[13px]">
                  {time ? <span className="text-slate-400 w-16 shrink-0">{time}</span> : null}
                  <span className="text-slate-700" style={{ wordBreak: 'break-word' }}>{name}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {day.hotelName ? (
        <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-3" data-testid="m-day-hotel">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Hotel tonight</p>
          <p className="text-[14px] font-semibold text-slate-900">{day.hotelName}</p>
          {(day.checkInDate || day.checkOutDate) ? (
            <p className="text-[12px] text-slate-500 mt-0.5">Check-in: {day.checkInDate || '—'} · Check-out: {day.checkOutDate || '—'}</p>
          ) : null}
        </div>
      ) : null}

      {meals.length > 0 && (
        <div className="mt-4" data-testid="m-day-meals">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-teal-600 mb-1">Meals included</p>
          <p className="text-[13px] text-slate-700">
            {meals.map((m) => (typeof m === 'object' && m ? (m.name || m.type || m.label || '') : String(m))).filter(Boolean).join(' · ')}
          </p>
        </div>
      )}
    </div>
  );
};

// Only the sections present on this itinerary are passed in. Hidden in the PDF
// (the caller gates on ?print=1). Flat .map of pills (no nested member maps) to
// stay clear of the visual-edits Babel crash.
const SectionJumpNav = ({ sections }) => {
  if (!sections || sections.length === 0) return null;
  const jump = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <div
      data-testid="section-jump-nav"
      style={{
        display: 'flex', gap: '6px', overflowX: 'auto',
        scrollbarWidth: 'none', msOverflowStyle: 'none',
        padding: '2px 0', marginBottom: '22px',
      }}
    >
      {sections.map((s) => (
        <button
          key={s.id}
          type="button"
          onClick={() => jump(s.id)}
          data-testid={`jump-${s.id}`}
          style={{
            flexShrink: 0, fontSize: '11px',
            border: '0.5px solid var(--color-border-tertiary, #e2e8f0)',
            borderRadius: '20px', padding: '4px 12px',
            background: '#ffffff', color: 'var(--color-text-secondary, #475569)',
            cursor: 'pointer', whiteSpace: 'nowrap',
            transition: 'background-color 0.15s, border-color 0.15s, color 0.15s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.borderColor = 'var(--color-border-tertiary, #e2e8f0)'; }}
        >
          {s.label}
        </button>
      ))}
    </div>
  );
};

// Loading-animation rebuild #3 — after downloading and inspecting the
// actual Dribbble GIF (dribbble.com/shots/4400319, Yup Nguyen) frame-by-
// frame, the reference is NOT a plane flying across the screen — it is a
// single monoline icon that MORPHS in place: plane -> car -> building
// (with a small tree) -> back to plane, looping forever, with two small
// cloud accents beside it. Rebuilt from scratch to match that concept using
// real SVG path morphing (flubber), not a lucide icon + trail. Hand-drawn
// custom path shapes (not a stock icon glyph), single LOGO-cyan (#5BC4CD)
// stroke throughout, centered on the page at a much larger size so it
// reads as a deliberate centerpiece rather than a small spinner.
const LOADER_VIEWBOX = '0 0 260 160';
const LOADER_PLANE_D = 'M245,75 L200,55 L140,52 L90,52 L60,15 L55,68 L100,90 L115,120 L155,95 L230,90 Z';
const LOADER_CAR_D = 'M28,112 L28,86 L58,86 L80,58 L152,58 L176,86 L232,86 L232,112 L214,112 L208,98 L192,98 L186,112 L84,112 L78,98 L62,98 L56,112 Z';
const LOADER_BUILDING_D = 'M70,124 L70,58 L98,58 L98,38 L162,38 L162,58 L190,58 L190,124 L138,124 L138,102 L122,102 L122,124 Z';

const loaderInterpolators = [
  interpolate(LOADER_PLANE_D, LOADER_CAR_D, { maxSegmentLength: 2 }),
  interpolate(LOADER_CAR_D, LOADER_BUILDING_D, { maxSegmentLength: 2 }),
  interpolate(LOADER_BUILDING_D, LOADER_PLANE_D, { maxSegmentLength: 2 }),
];
const LOADER_SEGMENT_S = 3;
const LOADER_CYCLE_S = LOADER_SEGMENT_S * 3;

const LoaderCloud = ({ style }) => (
  <svg viewBox="0 0 40 26" className="absolute h-8 w-12 md:h-10 md:w-16" style={style}>
    <path
      d="M10 22c-4 0-7-3-7-6.5S6 9 10 9c.6-3.6 3.8-6 7.5-6 4.1 0 7.5 3 8.2 7 3.6.3 6.3 3 6.3 6.5S28.8 22 25.5 22z"
      fill="none" stroke="#4A585F" strokeWidth="1.6" strokeLinejoin="round"
    />
  </svg>
);

const TravelLoadingAnimation = () => {
  const pathRef = useRef(null);
  const [phase, setPhase] = useState({ segment: 0, settled: 'from' });

  useEffect(() => {
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = ((now - start) / 1000) % LOADER_CYCLE_S;
      const segment = Math.min(2, Math.max(0, Math.floor(t / LOADER_SEGMENT_S)));
      const tLocal = t - segment * LOADER_SEGMENT_S;
      let progress = 0;
      let settled = 'from';
      if (tLocal < 1.05) {
        progress = 0;
        settled = 'from';
      } else if (tLocal < 1.95) {
        progress = (tLocal - 1.05) / 0.9;
        settled = null;
      } else {
        progress = 1;
        settled = 'to';
      }
      if (pathRef.current) {
        try {
          pathRef.current.setAttribute('d', loaderInterpolators[segment](progress));
        } catch (e) {
          // keep previous frame's shape rather than crash the page
        }
      }
      setPhase((prev) => (prev.segment === segment && prev.settled === settled ? prev : { segment, settled }));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const iconNow = phase.settled === 'from' ? phase.segment : phase.settled === 'to' ? (phase.segment + 1) % 3 : null;
  const showPlane = iconNow === 0;
  const showCar = iconNow === 1;
  const showBuilding = iconNow === 2;

  return (
    <div className="flex flex-col items-center justify-center gap-10 w-full" data-testid="travel-loading-animation">
      <div className="relative flex items-center justify-center" style={{ width: 'min(80vw, 480px)', height: '300px' }}>
        <LoaderCloud style={{ opacity: 0.5, top: '18%', left: '12%' }} />
        <LoaderCloud style={{ opacity: 0.35, bottom: '20%', right: '14%' }} />

        <svg viewBox={LOADER_VIEWBOX} className="relative z-10 h-40 w-64 md:h-52 md:w-80">
          <path ref={pathRef} d={LOADER_PLANE_D} fill="none" stroke="#5BC4CD" strokeWidth="5" strokeLinejoin="round" strokeLinecap="round" />

          {/* decorative details — fade in only once a shape has fully settled */}
          <g style={{ opacity: showPlane ? 1 : 0, transition: 'opacity 300ms ease' }}>
            <line x1="120" y1="66" x2="150" y2="66" stroke="#5BC4CD" strokeWidth="3" strokeLinecap="round" />
            <line x1="16" y1="46" x2="34" y2="46" stroke="#5BC4CD" strokeWidth="3" strokeLinecap="round" opacity="0.6" />
            <line x1="12" y1="86" x2="30" y2="86" stroke="#5BC4CD" strokeWidth="3" strokeLinecap="round" opacity="0.6" />
          </g>
          <g style={{ opacity: showCar ? 1 : 0, transition: 'opacity 300ms ease' }}>
            <circle cx="70" cy="115" r="9" fill="none" stroke="#5BC4CD" strokeWidth="4" />
            <circle cx="200" cy="115" r="9" fill="none" stroke="#5BC4CD" strokeWidth="4" />
            <line x1="14" y1="120" x2="34" y2="120" stroke="#5BC4CD" strokeWidth="3" strokeLinecap="round" opacity="0.6" />
          </g>
          <g style={{ opacity: showBuilding ? 1 : 0, transition: 'opacity 300ms ease' }}>
            {[0, 1, 2].map((row) => [0, 1].map((col) => (
              <rect key={`${row}-${col}`} x={98 + col * 42} y={52 + row * 22} width="12" height="12" rx="2" fill="none" stroke="#5BC4CD" strokeWidth="2.5" />
            )))}
            <line x1="40" y1="122" x2="40" y2="90" stroke="#5BC4CD" strokeWidth="3" strokeLinecap="round" opacity="0.7" />
            <circle cx="40" cy="82" r="12" fill="none" stroke="#5BC4CD" strokeWidth="3" opacity="0.7" />
          </g>
        </svg>
      </div>
      <p className="text-base font-semibold tracking-wide" style={{ color: '#4A585F' }} data-testid="loading-text">Preparing your itinerary…</p>
    </div>
  );
};

const SectionHeader = ({ icon, title, subtitle, number }) => (
  <div className="flex items-center gap-4">
    <div className="relative">
      <div className="w-10 h-10 md:w-14 md:h-14 bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl flex items-center justify-center text-white shadow-lg">
        {icon}
      </div>
      {number ? (
        <span className="absolute -top-2 -right-2 w-6 h-6 bg-[#C9973A] rounded-full flex items-center justify-center text-xs font-bold text-white shadow-md">
          {number}
        </span>
      ) : null}
    </div>
    <div>
      <h2 className="text-[15px] md:text-3xl font-heading font-bold text-slate-900">{title}</h2>
      <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
    </div>
  </div>
);

// Session 3a corrections pass — ONE sparse line-art compass accent
// (confirmed brand teal #158A68, ~7% opacity), replacing the generic
// lucide-icon watermark. Deliberately a single mark, not a repeating
// texture — proposed direction to review before use elsewhere.
const CompassAccent = () => (
  <svg
    data-testid="compass-accent"
    className="absolute top-6 right-6 md:right-10 w-28 h-28 md:w-36 md:h-36 pointer-events-none"
    style={{ opacity: 0.07 }}
    viewBox="0 0 100 100"
    fill="none"
    stroke="#158A68"
    strokeWidth="1"
  >
    <circle cx="50" cy="50" r="42" />
    <circle cx="50" cy="50" r="2" fill="#158A68" stroke="none" />
    <line x1="50" y1="4" x2="50" y2="16" />
    <line x1="50" y1="84" x2="50" y2="96" />
    <line x1="4" y1="50" x2="16" y2="50" />
    <line x1="84" y1="50" x2="96" y2="50" />
    <path d="M50 20 L56 50 L50 80 L44 50 Z" strokeWidth="1" />
  </svg>
);

const HotelCard = ({ hotel, index }) => {
  const isCruise = (hotel.accommodation_type || 'hotel') === 'cruise';
  const nights = isCruise
    ? (hotel.nights || getHotelNights(hotel.sailing_date, hotel.docking_date))
    : (hotel.nights || getHotelNights(hotel.checkInDate, hotel.checkOutDate));
  // Hotel names carry the option label appended (e.g. "… - Adults Only -
  // OPTION 1"). Strip everything from " - OPTION" onwards for display and
  // surface the option as its own badge.
  const rawName = hotel.hotelName || '';
  const cleanName = (rawName.split(/\s*-\s*OPTION/i)[0] || rawName).trim() || rawName;
  const optMatch = rawName.match(/-\s*OPTION\s*(\d+)/i);
  const optionNum = hotel.optionNumber ? String(hotel.optionNumber) : (optMatch ? optMatch[1] : null);
  const starNum = parseInt(hotel.starRating, 10);
  const stars = starNum >= 1 && starNum <= 7 ? starNum : 0;
  const board = hotel.mealPlanCustom || (hotel.mealPlan?.length > 0 ? hotel.mealPlan.join(', ') : '');
  // Correction 2 — rooms + pax composition are agent-controlled (legacy
  // fallbacks: roomCount / occupancy).
  const roomsRaw = hotel.rooms ?? hotel.roomCount;
  const roomsVal = roomsRaw ? `${roomsRaw} Room${Number(roomsRaw) === 1 ? '' : 's'}` : '';
  const paxComposition = hotel.pax_composition || hotel.occupancy || '';
  const shipDisplayName = hotel.ship_name || cleanName;
  const displayName = isCruise ? shipDisplayName : cleanName;
  const photo = resolveHotelPhoto(hotel);

  return (
    <div
      data-testid={`hotel-card-${index}`}
      className="flex flex-col sm:flex-row gap-4 p-4 bg-white border border-[#052E22]/15 rounded-sm"
      style={{ pageBreakInside: 'avoid', breakInside: 'avoid', marginTop: index > 0 ? '14px' : 0 }}
    >
      {/* Thumbnail — Session 3a corrections pass #2: only rendered when a
          real hotel photo exists. No photo => no box/placeholder at all,
          the text content simply takes the full card width. */}
      {photo.type === 'photo' && (
        <div className="w-full h-24 sm:w-28 sm:h-24 shrink-0 rounded-sm overflow-hidden" data-testid={`hotel-media-${index}`}>
          <img src={photo.url} alt={displayName || 'Accommodation'} className="w-full h-full object-cover" />
        </div>
      )}

      {/* Content */}
      <div className="flex-1 flex flex-col justify-between min-w-0">
        {/* Header row */}
        <div className="flex justify-between items-start gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-heading text-lg font-bold text-[#052E22] leading-tight" data-testid={`hotel-name-${index}`}>
                {displayName}
              </h3>
              {isCruise && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#0A5C48] uppercase tracking-widest" data-testid={`cruise-badge-${index}`}>
                  <Ship className="h-3 w-3" /> Cruise
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              {stars > 0 ? (
                <span className="text-[#C9973A] text-xs" data-testid={`hotel-stars-${index}`}>{'★'.repeat(stars)}</span>
              ) : hotel.starRating ? (
                <span className="text-xs font-semibold text-[#C9973A]" data-testid={`hotel-stars-${index}`}>{hotel.starRating}</span>
              ) : null}
              {!isCruise && hotel.city && (
                <span className="text-xs text-[#0A5C48] flex items-center gap-1"><MapPin className="h-3 w-3" /> {hotel.city}</span>
              )}
              {isCruise && hotel.cruise_line && (
                <span className="text-xs text-[#0A5C48]" data-testid={`cruise-line-${index}`}>{hotel.cruise_line}</span>
              )}
            </div>
          </div>
          {nights && (
            <span className="shrink-0 bg-[#C9973A] text-white text-[10px] px-2 py-1 uppercase tracking-widest font-bold rounded-sm" data-testid={`hotel-nights-badge-${index}`}>
              {nights} {nights === 1 ? 'Night' : 'Nights'}
            </span>
          )}
        </div>

        {/* Dense data grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          {isCruise ? (
            <>
              <SpecItem label="Sailing" value={hotel.sailing_date ? formatItinDate(hotel.sailing_date) : '—'} testId={`cruise-dates-${index}`} />
              <SpecItem label="Docking" value={hotel.docking_date ? formatItinDate(hotel.docking_date) : '—'} />
              <SpecItem label="Cabin / Deck" value={[hotel.cabin_type, hotel.deck].filter(Boolean).join(' · ') || '—'} />
              <SpecItem label="Embark / Disembark" value={[hotel.embarkation_port, hotel.disembarkation_port].filter(Boolean).join(' → ') || '—'} />
            </>
          ) : (
            <>
              <SpecItem label="Check-in" value={hotel.checkInDate ? formatItinDate(hotel.checkInDate) : '—'} />
              <SpecItem label="Check-out" value={hotel.checkOutDate ? formatItinDate(hotel.checkOutDate) : '—'} />
              <SpecItem label="Room Type" value={[hotel.roomType, roomsVal].filter(Boolean).join(', ') || '—'} sub={paxComposition || null} />
              <SpecItem label="Meal Plan" value={board || '—'} testId={`hotel-board-${index}`} />
            </>
          )}
        </div>

        {/* Footer row */}
        {(optionNum || hotel.extraBedIncluded || hotel.notes) && (
          <div className="mt-3 pt-3 border-t border-[#052E22]/10 flex items-center justify-between gap-3 text-xs text-[#344A51] flex-wrap">
            <div className="flex items-center gap-3 flex-wrap min-w-0">
              {hotel.extraBedIncluded && (
                <span className="inline-flex items-center gap-1.5 text-[#158A68]">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Extra Bed Included
                </span>
              )}
              {hotel.notes && (
                <RichDisplay html={hotel.notes} className="text-xs text-[#0A5C48] leading-snug italic" />
              )}
            </div>
            {optionNum && (
              <span className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-sm border border-[#158A68]/30 text-[10px] font-medium text-[#0A5C48] bg-[#158A68]/10 uppercase tracking-wide" data-testid={`hotel-option-${index}`}>Option {optionNum}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const SpecItem = ({ label, value, sub, testId }) => (
  <div className="min-w-0" data-testid={testId}>
    <div className="text-[9px] uppercase tracking-[0.15em] text-[#0A5C48]/60 font-semibold">{label}</div>
    <div className="text-sm font-medium text-[#052E22] truncate">{value}</div>
    {sub ? <div className="text-[11px] text-[#0A5C48]/60 truncate">{sub}</div> : null}
  </div>
);

// ── Cruise inclusions / exclusions (Correction 3) ─────────────────────────
// Mirrors the main Package Details "Editorial Ledger" restyle (Session 3a).
// Rendered as a separate card only when a cruise stay exists AND the
// agent has filled cruise inc/exc. Components hoisted (flat maps) to avoid the
// Babel nested-map crash.
const IncExcColumn = ({ title, items, variant }) => {
  const isInc = variant === 'inc';
  return (
    <div data-testid={`cruise-${variant}-column`}>
      <div className="flex items-center gap-4 mb-8">
        <div className={`flex items-center justify-center w-10 h-10 rounded-full ${isInc ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-500'}`}>
          {isInc ? <CheckCircle2 className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
        </div>
        <h3 className="font-heading text-2xl text-slate-900">{title}</h3>
      </div>
      <ul className="space-y-[18px]">
        {items.map((item, idx) => (
          <li key={`${variant}-${idx}-${String(item).slice(0, 10)}`} className="flex items-start gap-3.5 group">
            {isInc
              ? <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0 transition-transform group-hover:scale-110" />
              : <XCircle className="w-5 h-5 text-rose-400 mt-0.5 shrink-0 transition-transform group-hover:scale-110" />}
            <span className={`text-sm leading-relaxed ${isInc ? 'text-slate-700' : 'text-slate-500 group-hover:text-slate-600 transition-colors'}`}>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const CruiseIncExcSection = ({ inclusions, exclusions }) => {
  const incs = inclusions || [];
  const excs = exclusions || [];
  if (incs.length === 0 && excs.length === 0) return null;
  const both = incs.length > 0 && excs.length > 0;
  return (
    <section data-testid="cruise-inclusions-section" className="px-8 md:px-12 py-12 border-b border-slate-100 inclusion-block cruise-inc-block">
      <div className="relative z-10">
        <SectionHeader
          icon={<Ship className="h-6 w-6" />}
          title="Cruise Package"
          subtitle="What's Included & Not Included"
          number=""
        />
        <div className="relative bg-white border border-slate-200 rounded-2xl p-6 md:p-10 lg:p-12 mt-8">
          <div className={`grid ${both ? "grid-cols-1 md:grid-cols-2 md:after:content-[''] md:after:absolute md:after:top-0 md:after:bottom-0 md:after:left-1/2 md:after:-translate-x-1/2 md:after:w-px md:after:bg-slate-100" : 'grid-cols-1'} gap-10 md:gap-16 lg:gap-24 relative`}>
            {incs.length > 0 && <IncExcColumn title="Cruise Inclusions" items={incs} variant="inc" />}
            {excs.length > 0 && <IncExcColumn title="Cruise Exclusions" items={excs} variant="exc" />}
          </div>
        </div>
      </div>
    </section>
  );
};


// Pull the first number out of a free-text price string ("₹1,84,500",
// "EUR 725 Per Person"); null when none.
const extractAmount = (priceStr) => {
  const m = String(priceStr || '').match(/[\d,]+(?:\.\d+)?/);
  if (!m) return null;
  const n = parseFloat(m[0].replace(/,/g, ''));
  return Number.isNaN(n) ? null : n;
};

// ============================================================
// PRICING SECTION (Step 2) — pax-aware priced option cards.
// Consumes the shared `calc_price` util so the on-screen quote and the
// Playwright-rendered PDF stay in lockstep. Components are hoisted to the
// module scope (no nested inline .map() inside JSX) to dodge the Babel
// visual-edits "Maximum call stack" crash.
// ============================================================

// ₹ + en-IN grouping, no decimals (Unicode \u20B9 per spec).
const fmtINR = (n) => '\u20B9' + Math.round(Number(n) || 0).toLocaleString('en-IN');

const PAX_PILLS = [
  { key: 'person', label: 'Per person' },
  { key: 'couple', label: 'Per couple' },
  { key: 'family', label: 'Family (4)' },
  { key: 'group', label: 'Group (8+)' },
];

// A3 — which pax tabs an agent has chosen to expose, gated by traveller count.
// family needs >= 4 travellers (or the agent override), group needs >= 8.
const getVisiblePaxModes = (itinerary) => {
  const raw = Array.isArray(itinerary?.visible_pax_modes) && itinerary.visible_pax_modes.length > 0
    ? itinerary.visible_pax_modes
    : ['couple'];
  const travellers = Number(itinerary?.travellers)
    || ((Number(itinerary?.adults) || 0) + (Number(itinerary?.children) || 0) + (Number(itinerary?.infants) || 0))
    || 2;
  const override = !!itinerary?.show_family_group_tabs;
  const canShowFamily = travellers >= 4 || override;
  const canShowGroup = travellers >= 8 || override;
  const filtered = raw.filter((m) => {
    if (m === 'family') return canShowFamily;
    if (m === 'group') return canShowGroup;
    return true;
  });
  return filtered.length > 0 ? filtered : ['couple'];
};

const PaxToggle = ({ pills, paxMode, setPaxMode }) => (
  <div
    data-testid="pricing-pax-toggle"
    style={{ display: 'inline-flex', border: '0.5px solid var(--color-border-tertiary, #e2e8f0)', borderRadius: '8px', overflow: 'hidden' }}
  >
    {pills.map((p, i) => {
      const active = paxMode === p.key;
      return (
        <button
          key={p.key}
          type="button"
          onClick={() => setPaxMode(p.key)}
          data-testid={`pricing-pax-${p.key}`}
          style={{
            fontSize: '12px',
            padding: '6px 12px',
            fontWeight: active ? 500 : 400,
            color: active ? '#0f172a' : '#64748b',
            background: active ? 'var(--color-background-secondary, #f8fafc)' : '#ffffff',
            border: 'none',
            borderLeft: i === 0 ? 'none' : '0.5px solid var(--color-border-tertiary, #e2e8f0)',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {p.label}
        </button>
      );
    })}
  </div>
);

// C3/Book Now — resolve the amount the booking link should collect for one
// option, mirroring the backend (_booking_amount_for_option). The agent picks
// per option: advance (default 25%) / full amount / custom ₹.
const bookingAmountForOption = (itinerary, optionNum, r) => {
  const opt = ((itinerary.pricingOptions || [])[optionNum - 1]) || {};
  const btype = opt.booking_amount_type || 'advance';
  if (btype === 'full') return r.total;
  if (btype === 'custom') {
    const c = Number(opt.booking_custom_amount) || 0;
    return c > 0 ? c : r.advance;
  }
  return r.advance;
};

const bookAmountTypeLabel = (itinerary, optionNum) => {
  const opt = ((itinerary.pricingOptions || [])[optionNum - 1]) || {};
  const btype = opt.booking_amount_type || 'advance';
  const advPct = itinerary.advance_percentage != null ? Math.round(itinerary.advance_percentage * 100) : 25;
  if (btype === 'full') return 'full payment';
  if (btype === 'custom') return 'booking amount';
  return `${advPct}% advance`;
};

// Client-facing "Book Now" — creates a real payment link for THIS option and
// opens the branded booking page. Hidden in print (interactive); wrapped in
// .payment-link-section so the print CSS also strips it from the PDF.
const BookNowButton = ({ itinerary, optionNum, paxMode, amount, amountType, emphasized = true }) => {
  const [loading, setLoading] = useState(false);
  const id = itinerary.id || itinerary._id;
  const handle = async () => {
    // Open a tab synchronously inside the click gesture FIRST — mobile Safari
    // blocks window.open() that runs after an awaited network call. We then
    // redirect this tab once the payment link is created.
    const tab = window.open('about:blank', '_blank');
    setLoading(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/itinerary/${id}/payment-link`,
        { option: optionNum, pax_mode: paxMode || 'couple' },
      );
      const ref = res.data?.payment_ref;
      // Build the pay URL on the CURRENT origin so it always resolves on the
      // host the client is actually viewing (preview, crm.travcations.com, …),
      // never a domain that isn't live yet.
      const url = ref
        ? `${window.location.origin}/${ref}/pay`
        : (res.data?.link || res.data?.short_url || '');
      if (url) {
        if (tab) tab.location.href = url; else window.location.href = url;
      } else {
        if (tab) tab.close();
        toast.error('Could not start booking');
      }
    } catch (e) {
      if (tab) tab.close();
      toast.error(e?.response?.data?.detail || 'Could not start booking');
    } finally {
      setLoading(false);
    }
  };
  const typeLabel = amountType === 'full' ? 'full payment' : amountType === 'custom' ? 'booking amount' : 'advance';
  // Session 3b — Payment CTA uses the LOGO palette (#5BC4CD cyan / #4A585F
  // charcoal), not the primary page-structure teal, per the dual-palette
  // rule: this is a genuinely logo-adjacent brand moment.
  const baseBg = emphasized ? '#5BC4CD' : '#94a3b8';
  const hoverBg = emphasized ? '#4A585F' : '#64748b';
  const baseText = emphasized ? '#4A585F' : '#fff';
  const hoverText = emphasized ? '#fff' : '#fff';
  return (
    <div className="payment-link-section" style={{ marginTop: '12px', opacity: emphasized ? 1 : 0.85 }}>
      <button
        type="button"
        onClick={handle}
        disabled={loading}
        data-testid={`book-now-btn-${optionNum}`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          padding: '8px 18px', background: baseBg, color: baseText,
          border: 'none', borderRadius: '999px', fontSize: '13px', fontWeight: 600,
          cursor: loading ? 'wait' : 'pointer', transition: 'background-color 0.2s, color 0.2s',
          boxShadow: emphasized ? '0 1px 2px rgba(91,196,205,0.35)' : 'none',
        }}
        onMouseEnter={(e) => { if (!loading) { e.currentTarget.style.background = hoverBg; e.currentTarget.style.color = hoverText; } }}
        onMouseLeave={(e) => { e.currentTarget.style.background = baseBg; e.currentTarget.style.color = baseText; }}
      >
        <CreditCard className="h-3.5 w-3.5" />
        {loading ? 'Starting\u2026' : `Book Now \u00B7 \u20B9${Number(amount || 0).toLocaleString('en-IN')}`}
      </button>
      <div style={{ marginTop: '6px', fontSize: '11px', color: '#94a3b8' }}>
        {emphasized ? `Secure payment \u00B7 ${typeLabel}` : 'Tap this option above to select it'}
      </div>
    </div>
  );
};


// 1b — the itinerary itself doesn't carry a flight/package split (that lives
// on the booked trip, once one exists). We mirror ClientPortal's optional
// "Flights / Package" breakdown by applying the SAME ratio the agent set on
// the trip (trip.flightTotal : trip.packageTotal) to the live, pax-mode-aware
// total shown here — never a raw line-item breakdown, just the one split the
// agent explicitly opted into.
const FlightSplitRows = ({ itinerary, total }) => {
  if (!itinerary.showFlightSeparately) return null;
  const flightTotal = Number(itinerary.flightTotal) || 0;
  const packageTotal = Number(itinerary.packageTotal) || 0;
  const sum = flightTotal + packageTotal;
  if (sum <= 0) return null;
  const flightShare = Math.round(total * (flightTotal / sum));
  const packageShare = total - flightShare;
  return (
    <div data-testid="pricing-flight-split" style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: '#f8fafc', borderRadius: '8px' }}>
        <span style={{ fontSize: '12px', color: '#475569', fontWeight: 500 }}>Flights</span>
        <span style={{ fontSize: '12px', color: '#1e293b', fontWeight: 600 }}>{fmtINR(flightShare)}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: '#f8fafc', borderRadius: '8px' }}>
        <span style={{ fontSize: '12px', color: '#475569', fontWeight: 500 }}>Package</span>
        <span style={{ fontSize: '12px', color: '#1e293b', fontWeight: 600 }}>{fmtINR(packageShare)}</span>
      </div>
    </div>
  );
};

// Item 5 — Base → Discount → Final Payable breakdown, shown wherever a
// discount is actually set (discount_pct > 0). Shared by PriceCard (print)
// and StickyPricePanel (live) so the numbers always agree.
const DiscountBreakdown = ({ r, dark = false }) => {
  if (!r || !r.discount_pct) return null;
  const labelColor = dark ? 'rgba(255,255,255,0.55)' : '#94a3b8';
  const valueColor = dark ? '#fff' : '#0f172a';
  const discColor = dark ? '#7EE0C4' : '#059669';
  return (
    <div data-testid="pricing-discount-breakdown" style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: '12px' }}>
        <span style={{ color: labelColor }}>Base Price</span>
        <span style={{ color: valueColor, fontWeight: 500 }}>{fmtINR(r.base)}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: '12px' }}>
        <span style={{ color: discColor }}>{`Discount (${r.discount_pct}%${r.discount_label ? ` \u00B7 ${r.discount_label}` : ''})`}</span>
        <span style={{ color: discColor, fontWeight: 500 }}>{`-${fmtINR(r.discount_amount)}`}</span>
      </div>
    </div>
  );
};

// 1b — single lump-sum price card. No "Option N" label, no recommended/diff/
// selected comparison badges (there's only ever one price now) and never an
// itemised breakdown — matches ClientPortal's "one number or nothing" rule.
const PriceCard = ({ entry, r, itinerary, paxLabel, unboxed = false, bookMode = false, paxModeForApi }) => {
  const h = entry.hotel || {};
  const starNum = parseInt(h.starRating, 10);
  const stars = starNum >= 1 && starNum <= 7 ? starNum : 0;
  const boxStyle = unboxed
    ? { borderRadius: 0, padding: 0, background: 'transparent', border: 'none' }
    : { borderRadius: '12px', padding: '16px', background: '#ffffff', border: '0.5px solid var(--color-border-tertiary, #e2e8f0)' };
  return (
    <div data-testid={`pricing-card-${entry.optionNum}`} className="pdf-no-break" style={boxStyle}>
      {stars > 0 && (
        <div style={{ marginBottom: '6px' }}>
          <span style={{ color: '#D97706', fontSize: '12px', letterSpacing: '1px' }}>{'★'.repeat(stars)}</span>
        </div>
      )}
      <DiscountBreakdown r={r} />
      <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#94a3b8', fontWeight: 600, marginTop: r.discount_pct ? '8px' : 0 }}>Total Package Price</div>
      <div data-testid={`pricing-total-${entry.optionNum}`} style={{ fontSize: '28px', fontWeight: 700, color: '#0f172a', lineHeight: 1.1, marginTop: '2px' }}>{fmtINR(r.total)}</div>
      <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>{`${paxLabel} · incl. flights & tours`}</div>
      <FlightSplitRows itinerary={itinerary} total={r.total} />
      <p style={{ fontSize: '11px', fontStyle: 'italic', color: '#94a3b8', marginTop: '8px' }}>
        {`* Inclusive of GST${r.apply_tcs ? ' and applicable TCS' : ''}`}
      </p>
      {/* Print/PDF: advance shown prominently, no interactive button. */}
      <div className="print-only" style={{ display: 'none', marginTop: '10px', fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>
        {`Pay ${fmtINR(bookingAmountForOption(itinerary, entry.optionNum, r))} to book`}
        <span style={{ fontSize: '11px', fontWeight: 500, color: '#64748b', marginLeft: '6px' }}>
          {bookAmountTypeLabel(itinerary, entry.optionNum)}
        </span>
      </div>
      {bookMode && (
        <BookNowButton
          itinerary={itinerary}
          optionNum={entry.optionNum}
          paxMode={paxModeForApi}
          amount={bookingAmountForOption(itinerary, entry.optionNum, r)}
          amountType={(((itinerary.pricingOptions || [])[entry.optionNum - 1] || {}).booking_amount_type) || 'advance'}
          emphasized
        />
      )}
    </div>
  );
};

// Directive 2 (2026-09-21) — Trip-level dynamic pricingOptions (QuoteOption
// shape: optionId/label/summary/costItems/totals/isSelected). Purely
// additive — independent of, and does not touch, the itinerary's own
// single-price pricingOptions[0] path above/below (no regression there).
// Hidden entirely when 0 or 1 valid (optionId-bearing) option exists.
const TripOptionsCompareSection = ({ options }) => {
  const valid = (options || []).filter((o) => o && o.optionId);
  if (valid.length <= 1) return null;
  const sorted = [...valid].sort((a, b) => (b.isSelected ? 1 : 0) - (a.isSelected ? 1 : 0));
  return (
    <div className="mt-6 mb-6 px-8 md:px-12" data-testid="trip-options-compare-section">
      <h3 className="text-lg font-semibold text-slate-900 mb-3">Pricing Options</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {sorted.map((opt) => (
          <div
            key={opt.optionId}
            className={`rounded-xl border-2 p-4 ${opt.isSelected ? 'border-teal-400 bg-teal-50/60 ring-1 ring-teal-200' : 'border-slate-200 bg-white'}`}
            data-testid={`trip-option-compare-card-${opt.optionId}`}
          >
            <div className="flex items-center justify-between mb-1">
              <h4 className="font-semibold text-slate-900">{opt.label || 'Option'}</h4>
              {opt.isSelected && (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-teal-100 text-teal-800" data-testid={`trip-option-selected-badge-${opt.optionId}`}>
                  Selected
                </span>
              )}
            </div>
            {opt.summary && <p className="text-sm text-slate-500 mb-2">{opt.summary}</p>}
            {(opt.costItems || []).length > 0 && (
              <ul className="text-sm text-slate-600 mb-2 space-y-0.5 list-disc list-inside">
                {opt.costItems.map((ci, i) => (
                  <li key={i}>{ci.description || ci.category}</li>
                ))}
              </ul>
            )}
            {opt.totals ? (
              <div className="text-sm border-t border-slate-100 pt-2 mt-2 space-y-0.5">
                <div className="flex justify-between text-slate-600"><span>GST ({opt.totals.gstRate}%)</span><span>₹{Number(opt.totals.gstAmount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span></div>
                <div className="flex justify-between text-slate-600"><span>TCS ({opt.totals.tcsRate}%)</span><span>₹{Number(opt.totals.tcsAmount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span></div>
                <div className="flex justify-between font-bold text-teal-700"><span>Total</span><span>₹{Number(opt.totals.totalCost || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span></div>
              </div>
            ) : (
              <p className="text-xs text-slate-400 italic">Pricing not yet finalised</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const PricingSection = ({ itinerary, optionEntries, isPrintMode, paxMode, setPaxMode, unboxed = false }) => {
  // A3 — agent-controlled visible pax tabs (gated by traveller count).
  const visibleModes = getVisiblePaxModes(itinerary);
  const visiblePills = PAX_PILLS.filter((p) => visibleModes.includes(p.key));
  const showTabBar = visiblePills.length > 1;
  // Force the rendered mode into the allowed set (default = first visible mode).
  const screenPaxMode = visibleModes.includes(paxMode) ? paxMode : visibleModes[0];
  const effectivePaxMode = isPrintMode ? visibleModes[0] : screenPaxMode;
  const paxLabel = (PAX_CONFIG[effectivePaxMode] || PAX_CONFIG.couple).label;

  // Normalise pricingOptions so calc_price (reads pricingOptions[0].price as a
  // number) also works with the legacy free-text `items[0].price` shape.
  const pricingOptionsNumeric = (itinerary.pricingOptions || []).map((po) => {
    let p = po && po.price != null ? Number(po.price) : NaN;
    if (Number.isNaN(p)) {
      const item = (po && po.items ? po.items[0] : null) || {};
      p = extractAmount(item.price) || 0;
    }
    return { price: p, price_basis: (po && po.price_basis) || 'per_person' };
  });
  const pricedItinerary = { ...itinerary, pricingOptions: pricingOptionsNumeric };

  const entry = optionEntries[0];
  if (!entry) return null;
  const r = calc_price(pricedItinerary, effectivePaxMode);

  return (
    <section data-testid="pricing-section" className="pricing-section px-8 md:px-12 py-10">
      {!isPrintMode && showTabBar && (
        <div data-testid="pricing-controls" style={{ marginBottom: '16px' }}>
          <PaxToggle pills={visiblePills} paxMode={screenPaxMode} setPaxMode={setPaxMode} />
        </div>
      )}

      <div data-testid="pricing-cards" className="pdf-no-break grid grid-cols-1 gap-3 items-start">
        <PriceCard
          entry={entry}
          r={r}
          itinerary={itinerary}
          paxLabel={paxLabel}
          unboxed={unboxed}
          bookMode={!isPrintMode}
          paxModeForApi={effectivePaxMode}
        />
      </div>
    </section>
  );
};


// Corrections session (2026-08-27) — FX currency display helper. BATCH 2C
// Foreign-Currency quotes are a FIXED total in `quoteCurrency` — never
// re-derived in INR for the client view.
const FX_SYMBOLS = { USD: '$', EUR: '\u20AC', GBP: '\u00A3', SGD: 'S$', AED: 'AED ', JPY: '\u00A5', INR: '\u20B9' };
const fmtFx = (amount, currency) => `${FX_SYMBOLS[currency] || `${currency} `}${Number(amount || 0).toLocaleString('en-IN')}`;

// Corrections session — THE single place price + the payment CTA render on
// the live page (mobile: fixed bottom bar; desktop: floating bottom-right
// card). Reuses the exact same calc_price / getVisiblePaxModes / PAX_PILLS /
// PaxToggle / BookNowButton machinery PriceCard already used — no new
// pricing math, just one consolidated, correctly-labelled display:
//  - FX (BATCH 2C) quote → fixed total in its own currency, no GST note, no
//    auto payment link (none exists for FX) — CTA falls back to WhatsApp.
//  - Unified single-option quote, basis = total_package → "Total Package
//    Price", pax toggle hidden (switching pax count doesn't change a lump
//    sum).
//  - Unified single-option quote, basis = per_person/per_couple → the
//    agent-configured pax label (per person / per couple / per family /
//    per group), toggle shown only if the agent enabled >1 visible mode.
//  - Legacy totalCost-only itineraries (no pricingOptions) → totalCost +
//    quoteBasis, exactly as before.
const StickyPricePanel = ({ itinerary, coverEntries, onWhatsAppShare, onDownload, downloading }) => {
  const [paxMode, setPaxMode] = useState(null);
  const { totalCost, quoteBasis, paymentLink, paymentAmount } = itinerary;

  const isFx = (itinerary.quoteCurrencyMode || 'INR').toUpperCase() === 'FX';

  let amountLabel = null;
  let basisLabel = '';
  let ctaNode = null;
  let discountLine = null;

  if (isFx) {
    amountLabel = fmtFx(itinerary.fxClientAmount, itinerary.quoteCurrency || 'USD');
    basisLabel = 'Total Package Price';
    ctaNode = (
      <button
        type="button"
        onClick={onWhatsAppShare}
        data-testid="sticky-fx-contact-btn"
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold shadow-sm"
        style={{ background: '#5BC4CD', color: '#4A585F' }}
      >
        <MessageCircle className="h-3.5 w-3.5" /> Contact to Book
      </button>
    );
  } else if (coverEntries && coverEntries.length > 0) {
    const visibleModes = getVisiblePaxModes(itinerary);
    const screenPaxMode = paxMode && visibleModes.includes(paxMode) ? paxMode : visibleModes[0];
    const pricingOptionsNumeric = (itinerary.pricingOptions || []).map((po) => {
      let p = po && po.price != null ? Number(po.price) : NaN;
      if (Number.isNaN(p)) {
        const item = (po && po.items ? po.items[0] : null) || {};
        p = extractAmount(item.price) || 0;
      }
      return { price: p, price_basis: (po && po.price_basis) || 'per_person' };
    });
    const r = calc_price({ ...itinerary, pricingOptions: pricingOptionsNumeric }, screenPaxMode);
    amountLabel = fmtINR(r.total);
    if (r.discount_pct > 0) {
      discountLine = `Base ${fmtINR(r.base)} \u00B7 ${r.discount_pct}% off (-${fmtINR(r.discount_amount)})`;
    }
    const isLumpSum = r.price_basis === 'total_package';
    basisLabel = isLumpSum ? 'Total Package Price' : (PAX_CONFIG[screenPaxMode] || PAX_CONFIG.couple).label;
    const visiblePills = PAX_PILLS.filter((p) => visibleModes.includes(p.key));
    const showToggle = !isLumpSum && visiblePills.length > 1;
    ctaNode = (
      <>
        {showToggle && (
          <div className="mb-2" data-testid="sticky-pax-toggle">
            <PaxToggle pills={visiblePills} paxMode={screenPaxMode} setPaxMode={setPaxMode} />
          </div>
        )}
        <BookNowButton
          itinerary={itinerary}
          optionNum={1}
          paxMode={screenPaxMode}
          amount={bookingAmountForOption(itinerary, 1, r)}
          amountType={(((itinerary.pricingOptions || [])[0]) || {}).booking_amount_type || 'advance'}
          emphasized
        />
      </>
    );
  } else if (totalCost > 0) {
    amountLabel = `\u20B9${formatPrice(totalCost)}`;
    basisLabel = quoteBasis || 'Per Person';
    ctaNode = paymentLink ? (
      <a
        href={paymentLink}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="sticky-legacy-pay-btn"
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold shadow-sm"
        style={{ background: '#5BC4CD', color: '#4A585F' }}
      >
        Pay Now{paymentAmount && Number(paymentAmount) > 0 ? ` \u2014 ${'\u20B9'}${formatPrice(paymentAmount)}` : ''}
      </a>
    ) : null;
  }

  if (!amountLabel) return null;

  return (
    <>
      {/* Mobile — fixed bottom bar */}
      <div
        data-testid="sticky-price-panel-mobile"
        className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-slate-200 px-4 pt-3"
        style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))', boxShadow: '0 -8px 24px rgba(0,0,0,0.08)' }}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold truncate">{basisLabel}</p>
            {discountLine && (
              <p className="text-[10px] text-emerald-600 font-medium truncate" data-testid="sticky-discount-note-mobile">{discountLine}</p>
            )}
            <p className="text-lg font-bold text-slate-900 truncate" data-testid="sticky-price-amount-mobile">{amountLabel}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={onDownload}
              disabled={downloading}
              data-testid="sticky-download-pdf-mobile"
              aria-label="Download PDF"
              className="h-10 w-10 rounded-full border border-slate-200 flex items-center justify-center text-slate-500 disabled:opacity-50"
            >
              <Download className="h-4 w-4" />
            </button>
            {ctaNode}
          </div>
        </div>
      </div>

      {/* Desktop — flat full-width bottom strip. Session 3a corrections
          pass #3: pushed to the darkest confirmed teal (#052E22) since
          #0A5C48 still wasn't reading dark/heavy enough, padding trimmed
          further, and the price <p> no longer carries `font-heading`
          (Playfair) — it's a data value, not a heading, so it now inherits
          the page's confirmed body font (General Sans) like the rest of
          the strip. */}
      <div
        data-testid="sticky-price-panel-desktop"
        className="hidden md:block fixed bottom-0 left-0 right-0 w-full z-50 bg-[#052E22] border-t-4 border-[#C9973A] shadow-[0_-8px_30px_rgba(0,0,0,0.15)]"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-1 flex flex-row items-center justify-between gap-4">
          <div className="flex items-baseline gap-3 min-w-0">
            <div>
              <p className="text-[9px] uppercase tracking-[0.2em] text-teal-200/70 font-semibold leading-none">{basisLabel}</p>
              {discountLine && (
                <p className="text-[10px] text-emerald-300 font-medium leading-tight mt-0.5" data-testid="sticky-discount-note-desktop">{discountLine}</p>
              )}
              <p className="text-base md:text-lg font-bold text-white leading-tight" data-testid="sticky-price-amount-desktop">{amountLabel}</p>
            </div>
            {!isFx && <p className="text-[10px] italic text-teal-200/60 self-end pb-0.5">Inclusive of GST</p>}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {ctaNode}
            <button
              type="button"
              onClick={onDownload}
              disabled={downloading}
              data-testid="sticky-download-pdf-desktop"
              aria-label="Download PDF"
              className="p-1.5 text-teal-200 hover:text-white transition-colors disabled:opacity-50"
            >
              <Download className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};


const RichDisplay = ({ html, className = '' }) => {
  if (!html) return null;
  const isHtml = /<\/?[a-z][\s\S]*>/i.test(html);
  if (isHtml) {
    return (
      <div
        className={`prose prose-sm md:prose-base max-w-none prose-p:my-2 prose-ul:my-2 prose-li:my-0.5 prose-headings:font-heading text-slate-700 leading-relaxed ${className}`}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }
  return <p className={`text-slate-700 leading-relaxed whitespace-pre-line ${className}`}>{html}</p>;
};

// ── Day notes / caps handling ─────────────────────────────────────────────
// Agents type free-text day descriptions that often contain shouty ALL-CAPS
// "NOTE :" lines and inline caps fragments. We render NOTE lines as soft amber
// callouts (prefix stripped + sentence-cased) and tint inline ALL-CAPS phrases
// amber instead of SHOUTING at the traveller.
const NoteCallout = ({ text }) => (
  <div
    data-testid="day-note-callout"
    style={{
      borderLeft: '3px solid #D97706',
      background: '#FEF3C7',
      borderRadius: '0 8px 8px 0',
      padding: '10px 14px',
      fontSize: 13,
      color: '#92400E',
      marginTop: 12,
      lineHeight: 1.6,
    }}
  >
    {(text || '').replace(/^note\s*:\s*/i, '').toLowerCase().replace(/^\w/, (c) => c.toUpperCase())}
  </div>
);

// Multi-word ALL-CAPS run (e.g. "AT AN ADDITIONAL COST") OR a single caps word
// of 4+ letters (e.g. "MANDATORY") — short acronyms like GST/KMS/ID are skipped.
const CAPS_PHRASE_RE = /\b[A-Z][A-Z'’]+(?:\s+[A-Z][A-Z'’]*)+\b|\b[A-Z]{4,}\b/g;
const highlightCaps = (text) => {
  const out = [];
  let last = 0;
  let i = 0;
  let m;
  CAPS_PHRASE_RE.lastIndex = 0;
  while ((m = CAPS_PHRASE_RE.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(<span key={`caps-${i}`} style={{ color: '#B45309', fontWeight: 600 }}>{m[0]}</span>);
    i += 1;
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out.length ? out : text;
};

// Normalise a description (plain text, or light HTML) into trimmed line segments.
const toDaySegments = (raw) => {
  if (!raw) return [];
  let text = String(raw);
  if (/<\/?[a-z][\s\S]*>/i.test(text)) {
    text = text
      .replace(/<\s*br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|li|h[1-6])\s*>/gi, '\n')
      .replace(/<li[^>]*>/gi, '• ')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&');
  }
  return text.split(/\n+/).map((s) => s.trim()).filter(Boolean);
};

const DayDescription = ({ text }) => {
  const segments = toDaySegments(text);
  if (segments.length === 0) return null;
  return (
    <div data-testid="day-description">
      {segments.map((seg, idx) => (
        /^\s*note\s*:/i.test(seg)
          ? <NoteCallout key={`seg-${idx}`} text={seg} />
          : (
            <p
              key={`seg-${idx}`}
              className="text-slate-700 leading-relaxed mb-2 last:mb-0"
              style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
            >
              {highlightCaps(seg)}
            </p>
          )
      ))}
    </div>
  );
};

// Session 2 — full-width banner heading a destination leg (a run of
// consecutive days resolved to the same hotel city). Clipped to the parent
// card's rounded-xl corners via the parent's `overflow-hidden`.
// Corrections session — approved "trip route map" strip for multi-
// destination trips. Reuses Session 2's destGroups (same sequence-based
// city grouping) — no new grouping logic, purely a display strip.
const RouteMapStrip = ({ groups }) => {
  if (!groups || groups.length < 2) return null;
  return (
    <div data-testid="route-map-strip" className="flex items-center gap-2 flex-wrap mb-6">
      {groups.map((g, i) => (
        <React.Fragment key={`route-${i}`}>
          <span
            data-testid={`route-stop-${i}`}
            style={{
              fontSize: '12px', fontWeight: 600, color: '#0F6E56',
              background: '#ECFDF5', border: '1px solid #A7F3D0',
              borderRadius: '20px', padding: '5px 14px', whiteSpace: 'nowrap',
            }}
          >
            {g.city || 'Journey'}
          </span>
          {i < groups.length - 1 && (
            <span style={{ color: '#94a3b8', fontSize: '13px' }} aria-hidden="true">&rarr;</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

const DestinationBanner = ({ group, index, destination, destinationPhotoOverrides }) => {
  const banner = resolveGroupBanner(group, destination, destinationPhotoOverrides);
  const label = group.city || 'Your Journey';
  const nightsLabel = `${group.days.length} Day${group.days.length === 1 ? '' : 's'}`;
  return (
    <div
      data-testid={`destination-banner-${index}`}
      className="relative h-48"
      style={banner.type === 'photo'
        ? { backgroundImage: `url(${banner.url})`, backgroundSize: 'cover', backgroundPosition: 'center' }
        : { background: 'linear-gradient(160deg, #134E4A 0%, #0F766E 100%)' }}
    >
      {banner.type === 'photo' ? (
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
      ) : (
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{ backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)', backgroundSize: '20px 20px' }}
        />
      )}
      <div className="absolute bottom-0 left-0 p-5">
        <p className="font-heading text-2xl font-semibold text-white drop-shadow-md">{label}</p>
        <p className="text-white/70 text-xs mt-1">{nightsLabel}</p>
      </div>
    </div>
  );
};

// Session 2 — collapsed by default (Date · Day N · Title, 1px border-b,
// minimal padding); tap to expand into the full description/photo. Uses
// framer-motion for the smooth vertical reveal. This is the LIVE-preview
// path only — the PDF renderer keeps the always-expanded DayCard below
// (Session 5 will bring PDF parity).
const CollapsibleDayCard = ({ day, dayDate, isLast }) => {
  const [open, setOpen] = useState(false);
  const dateLabel = dayDate ? formatItinDate(dayDate) : '';
  return (
    <div
      className="day-card"
      data-testid={`day-card-${day.dayNumber}`}
      style={{ borderBottom: isLast ? 'none' : '1px solid #e2e8f0' }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`day-card-toggle-${day.dayNumber}`}
        className="w-full flex items-center gap-3.5 text-left"
        style={{ padding: '16px 2px', background: 'none', border: 'none', cursor: 'pointer' }}
      >
        <div
          style={{
            width: '40px', height: '40px', minWidth: '40px', borderRadius: '12px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '14px', fontWeight: 700, color: '#ffffff', backgroundColor: '#14b8a6',
          }}
        >
          {day.dayNumber}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span style={{ fontSize: '11px', fontWeight: 600, color: '#0F6E56', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Day {day.dayNumber}
            </span>
            {dateLabel && <span style={{ fontSize: '12px', color: '#94a3b8' }}>· {dateLabel}</span>}
          </div>
          <h3 className="font-heading" style={{ fontSize: '16px', fontWeight: 600, color: '#0f172a', marginTop: '2px', wordBreak: 'break-word' }}>
            {day.title}
          </h3>
        </div>
        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }} style={{ flexShrink: 0 }}>
          <ChevronDown className="h-4 w-4 text-slate-400" />
        </motion.div>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
            data-testid={`day-card-content-${day.dayNumber}`}
          >
            <div style={{ padding: '0 2px 20px 53.5px' }}>
              <DayDescription text={day.description} />
              {day.imageUrl && (
                <div className="mt-4 rounded-xl overflow-hidden">
                  <img
                    src={day.imageUrl.startsWith('/') ? `${BACKEND_URL}${day.imageUrl}` : day.imageUrl}
                    alt={day.title}
                    loading="lazy"
                    className="w-full h-56 md:h-64 object-cover"
                  />
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const DayCard = ({ day, isLast }) => (
  <div
    className="relative pdf-no-break day-card"
    style={{
      // Explicit pixel-based flex container — html2pdf/html2canvas does not
      // reliably compute Tailwind's `flex gap-5` so we inline the layout.
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: '20px',
      pageBreakInside: 'avoid',
      breakInside: 'avoid',
      paddingTop: '8px',
      marginBottom: '20px',
    }}
  >
    {/* Timeline column — fixed 48 px so the connector line lands on the
        centre of the 48 px badge in both browser and PDF. */}
    <div
      className="pdf-no-break"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '48px',
        flexShrink: 0,
        marginTop: '8px',
      }}
    >
      <div
        className="bg-gradient-to-br from-teal-500 to-emerald-600 text-white font-bold shadow-md"
        style={{
          width: '48px',
          height: '48px',
          minWidth: '48px',
          minHeight: '48px',
          borderRadius: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '16px',
          lineHeight: '1',
          zIndex: 10,
          // Solid fallback bg so html2canvas always paints something even
          // if the gradient fails on the canvas engine.
          backgroundColor: '#14b8a6',
        }}
      >
        {day.dayNumber}
      </div>
      {!isLast && (
        <div
          className="bg-gradient-to-b from-teal-300 to-transparent"
          style={{
            width: '2px',
            flex: '1 1 auto',
            minHeight: '40px',
            marginTop: '4px',
            backgroundColor: '#5eead4',
          }}
        ></div>
      )}
    </div>

    {/* Content column — explicit flex:1 plus min-width:0 so long words /
        URLs in the description don't push the column wider than the page
        in html2pdf (root cause of the VCH_0526_3E4B3 overflow bug). */}
    <div
      style={{
        flex: '1 1 0%',
        minWidth: 0,
        paddingBottom: '24px',
        overflow: 'hidden',
      }}
    >
      <div
        className="bg-white border border-slate-200 shadow-sm pdf-no-break"
        style={{
          borderRadius: '16px',
          padding: '20px',
          overflow: 'hidden',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
        }}
      >
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-semibold text-teal-600 uppercase tracking-wider">Day {day.dayNumber}</span>
        </div>
        <h3 className="font-heading text-xl font-bold text-slate-900 mb-3" style={{ wordBreak: 'break-word' }}>{day.title}</h3>

        <div style={{ overflow: 'hidden', wordBreak: 'break-word', overflowWrap: 'break-word', maxWidth: '100%' }}>
          <DayDescription text={day.description} />
        </div>

        {day.imageUrl && (
          <div className="mt-4 rounded-xl overflow-hidden">
            <img
              src={day.imageUrl.startsWith('/') ? `${BACKEND_URL}${day.imageUrl}` : day.imageUrl}
              alt={day.title}
              loading="lazy"
              className="w-full h-56 md:h-64 object-cover"
            />
          </div>
        )}
      </div>
    </div>
  </div>
);

export default ItineraryPreviewPage;
